import SchnellbuehnePanel from './panels/SchnellbuehnePanel';
import BuehnenArbeitsflaeche from './BuehnenArbeitsflaeche';
import { buehnenStapel, type AngehefteteBuehne } from '../lib/buehnenAnheften';
import type { BuehnenBezug } from '../../shared/buehnen-anordnung';
import { ShareStageButton } from './StageSharingLive';
import { useCallback, useRef, useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { Layout, Model, TabNode, TabSetNode, BorderNode, IJsonModel, ITabSetRenderValues, ITabRenderValues, Actions, DockLocation, Rect, Action } from 'flexlayout-react';
import type { CuiStates } from '../types';
import { copyToClipboard } from '../utils/clipboard';
import { logCuiTelemetry, newCorrelationId } from '../lib/cuiTelemetry';
import { isSubSession } from '../utils/subSession';
import { isOpenChat } from '../utils/chatLifecycle';
import { devPortUrl } from '../lib/devPortUrl';
import { isShadowClient } from '../lib/shadowClient';
import { useAuth } from '../contexts/AuthContext';
import { ACCOUNTS } from '../types';
const ACCOUNT_LABELS: Record<string, string> = Object.fromEntries(ACCOUNTS.map(a => [a.id, a.label]));

// --- flexlayout-react CPU fix ---
// flexlayout's internal useLayoutEffect hooks (no dep arrays) call getBoundingClientRect
// on every render and trigger redrawInternal() when sub-pixel float comparisons fail.
// This creates a continuous render loop consuming 100%+ CPU on a single core.
// Fix: Tolerance-based comparison stops re-render triggers from sub-pixel float jitter.
// Both equals() and equalSize() use strict === on getBoundingClientRect floats.
// equalSize() is critical: it's used in arePropsEqual to decide if tab CONTENT re-renders.
// Without this patch, ALL visible tab content re-renders on every flexlayout frame.
//
// The tolerance MUST scale with zoom. getBoundingClientRect is affected by browser/OS
// zoom, which snaps layout to physical pixels; one physical pixel is >0.5 CSS px under
// zoom (e.g. ~0.67 at 150%). A fixed 0.5 tolerance then fails under zoom → the redraw
// loop this patch suppresses resumes, re-rendering tab content continuously, which
// resets scroll to the top-left (reported by a partner working zoomed-in on a small
// screen). Scale by devicePixelRatio, but never below the proven 0.5 (so dpr=1 — the
// no-zoom default — is byte-for-byte unchanged). Read live so runtime zoom is tracked.
function rectTolerance(): number {
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  return 0.5 * Math.max(1, dpr);
}
Rect.prototype.equals = function patchedEquals(rect: Rect | undefined) {
  if (!rect) return false;
  const t = rectTolerance();
  return Math.abs(this.x - rect.x) < t
    && Math.abs(this.y - rect.y) < t
    && Math.abs(this.width - rect.width) < t
    && Math.abs(this.height - rect.height) < t;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- patching library method
const rectProto = Rect.prototype as any;
rectProto.equalSize = function patchedEqualSize(rect: Rect | undefined) {
  if (!rect) return false;
  const t = rectTolerance();
  return Math.abs(this.width - rect.width) < t
    && Math.abs(this.height - rect.height) < t;
};

// Throttle LayoutInternal.redrawInternal to max 4 calls/sec.
// Each call triggers full LayoutInternal render → useLayoutEffect hooks → getBoundingClientRect
// → forced synchronous browser layout. At 60fps this consumes 100% CPU.
// Layout ref → Layout class → selfRef → LayoutInternal (where redrawInternal lives).
interface FlexLayoutInternal {
  _redrawPatched?: boolean;
  redrawInternal?: (reason?: string) => void;
}
interface PatchableLayoutRef {
  selfRef?: { current?: FlexLayoutInternal };
}
function patchLayoutRedraw(layoutRef: PatchableLayoutRef | null) {
  const internal = layoutRef?.selfRef?.current;
  if (!internal || internal._redrawPatched) return;
  const orig = internal.redrawInternal;
  if (typeof orig !== 'function') return;
  let scheduled = false;
  internal.redrawInternal = (reason?: string) => {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => { scheduled = false; orig.call(internal, reason); }, 250);
  };
  internal._redrawPatched = true;
}
// --- Critical-path panels (lightweight, needed immediately) ---
import CuiLitePanel from './panels/CuiLitePanel';
import ImageDrop from './panels/ImageDrop';
import BrowserPanel from './panels/BrowserPanel';
import FilePreview from './panels/FilePreview';
import NotesPanel from './panels/NotesPanel';
import ErrorBoundary from './ErrorBoundary';
import SharedPanelLoader from './panels/shared/PanelLoader';
import PanelConnectivityGuard from './panels/PanelConnectivityGuard';

// --- Heavy panels — aus Panel Registry (Single Source of Truth) ---
// Neues Panel? panelRegistry.ts editieren, NICHT diese Datei.
import {
  MissionControl, OfficePanel, KnowledgeFullscreen,
  LinkedInPanel, BridgeMonitor, InfisicalMonitor, QADashboard, RepoDashboard,
  WatchdogPanel, BackgroundOpsPanel,
  ConversationQueuePanel, MaintenancePanel, UserInputAuditPanel,
  ArchitectureExplorer, ReportBuilder, PromptExplorer, BusinessAngelPanel, PrivatAngelPanel,
  MyTasksPanel, FocusPanel, ActivityFeedPanel, EnergyCases, ReportCases, PartnerInboxPanel, GroupChatPanel, DeckAnnotatePanel, ReportAnnotatePanel, CustomerCommsPanel,
  NetzwerkPanel,
  FeedbackPanel, TeamStatusPanel, BusinessDocsPanel, BusinessNavigatorPanel, SalesPanel, BusinessOverviewPanel, UploadPanel, ToolHub,
  CalendarPanel, MailPanel, PostfaecherPanel, ErrorMonitor, PartnerServerPanel, PartnerBrowsersPanel, SandboxAngelPanel,
  PlatformAdmin, DeployPanel, ProdChatPanel,
  PANEL_NAMES, menuGroupsForRole,
} from './panelRegistry';
// LayoutBuilder ist Desktop-only — bleibt hier
const LayoutBuilder = lazy(() => import('./LayoutBuilder'));

import '../styles/office.css';
import { conversationsUrl } from '../utils/conversationScope';
import { aufnahmeHintergrund, farbenFuerHtml2canvas } from '../lib/aufnahmeFarben';
import { liesStandardOrdner, startOrdner } from '../lib/dateiStandardOrdner';

/*
 * Der Lade-Platzhalter kommt aus panels/shared/PanelLoader — EINE Fassung.
 *
 * Bis 07.09. gab es ihn zweimal, wortgleich: hier und dort. An diese Kopie hatte
 * ich am Vormittag die Kennung `data-ai-id="panel-laedt"` gehaengt, mit der eine
 * Aufnahme auf fertig gezeichnete Kacheln wartet — die andere Fassung (Direktlink
 * /?panel=…) blieb dabei unmarkiert und waere von der Pruefung stillschweigend
 * uebersehen worden. Genau die Sorte Luecke, gegen die die Pruefung gebaut ist.
 * Der Text bleibt derselbe; er wird jetzt als `message` hereingereicht.
 */
const PanelLoader = () => <SharedPanelLoader message="Loading panel..." />;
const API = '/api';

// Map workspace to default browser URL for new panels.
// devPortUrl picks subdomain proxy on partner / direct host:port on dev — see lib/devPortUrl.ts.
const WORKSPACE_BROWSER_PORTS: Record<string, number> = {
  "engelmann-ai-hub": 3009,
  "engelmann-dashboards": 4800,
  "engelmann-developer": 3009,
  "werking-energy": 3007,
  "werking-report": 3008,
  "werkingsafety": 3006,
};
// Some workspaces should default to the PRODUCTION app instead of the shared
// demo instance on its dev port. The production host is rendered transparently
// via the *-live proxy (see BrowserPanel LIVE_PROXY_HOSTS). Partner host only —
// on dev the port default is kept so local app testing is unaffected.
const ON_PARTNER_HOST =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'partner.werking.tools' ||
    window.location.hostname.endsWith('.partner.werking.tools'));
const WORKSPACE_BROWSER_URL_OVERRIDES: Record<string, string> = ON_PARTNER_HOST
  ? {
      "werking-energy": "https://energy.werking.tools",
      "werking-report": "https://report.werking.tools",
    }
  : {};
const WORKSPACE_BROWSER_URLS: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(WORKSPACE_BROWSER_PORTS).map(([ws, port]) => [ws, devPortUrl(port)])
  ),
  ...WORKSPACE_BROWSER_URL_OVERRIDES,
};

/**
 * Der letzte Pfadteil des Arbeitsbereichs — die Kennung, unter der die Vorbelegung
 * einer Browser-Adresse nachschlaegt.
 *
 * OHNE ARBEITSBEREICH GIBT ES KEINE KENNUNG, und das ist kein Fehler: eine Buehne des
 * anderen Hauses wird bewusst ohne Pfad gebaut (siehe BuehnenVorschau). Bis 10.09.2026
 * stand hier blank `workDir.split('/')`, und der undefinierte Wert riss die ganze
 * Flaeche mit „Cannot read properties of undefined (reading 'split')" ab — jede
 * Partner-Buehne mit Browser-Kachel, seit dem 09.09.
 *
 * Zurueckgegeben wird eine LEERE Kennung, kein geratener Pfad. Die Folge ist genau
 * eine: die Vorbelegung findet nichts, und eine Kachel ohne eigene Adresse bleibt
 * leer. Das steht dann auch so auf der Buehne.
 */
function arbeitsbereichsKennung(workDir?: string): string {
  return workDir ? workDir.split('/').pop() || '' : '';
}

function defaultLayout(workDir?: string): IJsonModel {
  // Standard layout:
  //   [ Chat (top)             | Tool Hub ]
  //   [ Browser (workspace-App)|          ]
  const wsId = arbeitsbereichsKennung(workDir);
  const port = WORKSPACE_BROWSER_PORTS[wsId];
  const browserUrl = WORKSPACE_BROWSER_URLS[wsId] || (port ? devPortUrl(port) : '');
  return {
    global: {
      tabEnableClose: true,
      tabEnablePopout: false,
      tabSetEnableMaximize: true,
      tabSetEnableDrop: true,
      tabSetEnableDrag: true,
      tabSetEnableDivide: true,
      // Render every tab on mount instead of waiting for it to become visible.
      // Lets the headless browser-panel API (eval/click/type/...) reach
      // background tabs — without this, only the currently-selected tab in
      // each tabset is in the DOM and the others silently timeout.
      tabEnableRenderOnDemand: false,
      splitterSize: 4,
      tabSetMinWidth: 200,
      tabSetMinHeight: 150,
    },
    borders: [],
    layout: {
      type: 'row',
      weight: 100,
      children: [
        {
          // Left column: Chat top, Browser bottom (workspace-specific App)
          type: 'row',
          weight: 70,
          children: [
            {
              type: 'tabset',
              weight: 60,
              children: [
                { type: 'tab', name: 'Chat', component: 'cui', config: {} },
              ],
            },
            {
              type: 'tabset',
              weight: 40,
              children: [
                { type: 'tab', name: 'Browser', component: 'browser', config: { url: browserUrl } },
              ],
            },
          ],
        },
        {
          // Right column: Tool Hub
          type: 'tabset',
          weight: 30,
          children: [
            { type: 'tab', name: 'Tool Hub', component: 'tool-hub', config: {} },
          ],
        },
      ],
    },
  };
}

// How long a user-closed panel stays "tombstoned" — long enough for the user's own
// layout POST to propagate and outlast a colliding 409 / apply-layout echo.
const TOMBSTONE_MS = 45000;
// Finished chat sessions need a much longer tombstone than component tombstones: the
// stale layout snapshot that resurrects them (another window's queued save, a delayed
// broadcast, a reconnect replaying an old server layout) can arrive minutes or hours
// later, not just seconds. Observed 2026-08-01: a session got auto-unfinished via a
// resurrected tab 3.5h after being finished. Safe to keep long — a deliberate user
// reopen (clicking the session in "Alle", starting a new message) never goes through
// stripTombstonedTabs; only the passive background layout-sync path does.
const SESSION_TOMBSTONE_MS = 30 * 60_000;
// How long after an explicit user layout action (Layout button, unstack, builder apply,
// reset) the LOCAL layout wins a concurrent server-version race.
const USER_LAYOUT_ACTION_MS = 8000;
// A passive layout edit (dragging a splitter to resize a panel, moving a tab) is just as
// much "the user is shaping THIS window's layout" as an explicit Layout-button action — so
// it must also win a concurrent server-version race, otherwise the resize-triggered save
// hits a 409 (or a cross-window apply-layout broadcast) and the whole arrangement gets
// replaced by the server's structurally-different layout mid-drag. (Kurt, 2026-06-24:
// "Rahmen auf B1 vergrößert → Ansicht springt auf B2".) Shorter than the explicit window:
// just long enough to outlast the 1500ms save debounce + the 409 round-trip + the
// cross-window WS echo, but short enough not to keep rejecting legit admin/auto-layout
// pushes seconds later.
const PASSIVE_LAYOUT_EDIT_MS = 4000;

// Recursively drop `tab` nodes whose component the user just closed (tombstoned) OR whose
// chat session was just finished (session-tombstoned), then prune now-empty tabsets/rows.
// A stale server layout (409 / control:apply-layout) must not resurrect a panel the user
// explicitly closed, nor a chat the user just finished. Defensive: returns the ORIGINAL
// object untouched if nothing is tombstoned or if stripping would leave zero tabs —
// never produce a broken empty layout.
function stripTombstonedTabs(
  layoutJson: IJsonModel,
  isTombstoned: (component: string) => boolean,
  isSessionTombstoned: (sessionId: string) => boolean,
): IJsonModel {
  if (!layoutJson?.layout) return layoutJson;
  let removed = 0;
  let kept = 0;
  const prune = (node: unknown): unknown => {
    if (!node || typeof node !== 'object') return node;
    const n = node as { type?: string; component?: string; config?: Record<string, unknown>; children?: unknown[] };
    if (n.type === 'tab') {
      const comp = typeof n.component === 'string' ? n.component : '';
      if (comp && isTombstoned(comp)) { removed++; return null; }
      const cfg = n.config || {};
      const cfgSid = typeof cfg.initialSessionId === 'string' ? cfg.initialSessionId : '';
      const routeSid = typeof cfg._route === 'string' && cfg._route.startsWith('/c/') ? cfg._route.slice(3) : '';
      const sid = cfgSid || routeSid;
      if (sid && isSessionTombstoned(sid)) { removed++; return null; }
      kept++;
      return n;
    }
    if (Array.isArray(n.children)) {
      const children = n.children.map(prune).filter((c) => c !== null);
      if ((n.type === 'tabset' || n.type === 'row') && children.length === 0) return null;
      return { ...n, children };
    }
    return n;
  };
  const clone: IJsonModel = JSON.parse(JSON.stringify(layoutJson));
  const prunedRoot = prune(clone.layout);
  if (removed === 0) return layoutJson;            // nothing closed — pass through
  if (kept === 0 || !prunedRoot) return layoutJson; // would empty layout — keep original
  clone.layout = prunedRoot as IJsonModel['layout'];
  return clone;
}

// User clicked the Layout button → every active chat panel should sit at EQUAL
// width side by side, not "one big, one tiny". FlexLayout's split-add
// (addNode DockLocation.RIGHT) gives the new tabset an arbitrary weight, so
// after a few mounts the chat columns drift to wildly uneven widths. Rebalance
// only chat tabsets that share the same parent row (so an unrelated Browser /
// Tool-Hub column keeps its size). Skips groups that are already balanced so the
// 30s sync loop doesn't thrash the layout or the save endpoint.
// Returns how many tabsets were re-weighted (0 ⇒ nothing to save).
function equalizeCuiTabsets(m: Model): number {
  const byParent = new Map<string, TabSetNode[]>();
  m.visitNodes((node) => {
    if (node.getType() !== 'tabset') return;
    const ts = node as TabSetNode;
    const holdsCui = ts.getChildren().some((t) => {
      const c = (t as TabNode).getComponent?.();
      return c === 'cui' || c === 'cui-lite';
    });
    if (!holdsCui) return;
    const parentId = ts.getParent()?.getId();
    if (!parentId) return;
    const arr = byParent.get(parentId) ?? [];
    arr.push(ts);
    byParent.set(parentId, arr);
  });
  let changed = 0;
  const EQUAL_WEIGHT = 100;
  for (const tabsets of byParent.values()) {
    if (tabsets.length < 2) continue; // single chat column: nothing to balance
    const weights = tabsets.map((ts) => ts.getWeight() ?? 0);
    if (Math.max(...weights) - Math.min(...weights) < 0.5) continue; // already balanced
    for (const ts of tabsets) {
      try {
        m.doAction(Actions.updateNodeAttributes(ts.getId(), { weight: EQUAL_WEIGHT }));
        changed++;
      } catch (err) { console.warn('[LM] equalize weight failed:', err); }
    }
  }
  return changed;
}

interface ActivationPlan {
  projectId: string;
  conversations: Array<{ sessionId: string; accountId: string }>;
}

interface LayoutManagerProps {
  projectId: string;
  layoutKey?: string;
  /** Fehlt bei einer Buehne des anderen Hauses — dort kennt der Server keinen Pfad. */
  workDir?: string;
  cuiStates?: CuiStates;
  onAttentionChange?: (needsAttention: boolean, state?: 'working' | 'needs_attention' | 'idle') => void;
  onCuiStateReset?: (cuiId: string) => void;
  pendingActivation?: ActivationPlan[] | null;
  onActivationProcessed?: (projectId?: string) => void;
  isActive?: boolean;
  onMissingSessions?: (count: number) => void;
  /**
   * DIE BUEHNE: das Fenster EINER Entscheidung, als Ueberlagerer.
   *
   * Ein flexlayout-Dokument, gebaut aus dem Rezept der Karte (GuidePanel:
   * layoutAusRezept). Es wird NICHT gespeichert und NICHT in das Layout des
   * Arbeitsbereichs geschrieben — es lebt, solange die Karte offen ist, und wird
   * mit ihr verworfen. Rafael am 06.09.2026: „jede Buehne ist ein eigenes Layout —
   * dort werden nur Sachen gemountet."
   *
   * Warum hier und nicht in App: der `factory` unten kennt `projectId` und `workDir`
   * und ist die einzige Stelle, die eine Kachel bauen kann. Die Buehne erbt damit den
   * Kontext des Arbeitsbereichs, ohne dessen Layout je zu beschreiben.
   *
   * Der Grund, warum es das gibt: bis 06.09. schrieb das Rezept in
   * `layouts/<workspace>.json`. Ein Arbeitsbereich hat EIN Layout, also konnte von N
   * offenen Karten immer nur die zuletzt hergerichtete passen — fuenf Karten stritten
   * an dem Abend um `werking-report`, sechs Fenster-Bestaetigungen wurden dadurch
   * falsch, und ein Livelock blieb nur aus, weil Sitzungen sich weigerten. Mit der
   * Buehne kann das nicht mehr eintreten: es gibt nichts Geteiltes mehr.
   */
  buehne?: unknown | null;
  buehnenBezug?: BuehnenBezug;
  /**
   * Die angeheftete Buehne, falls sie in DIESEM Arbeitsbereich wohnt (lib/buehnenAnheften.ts).
   * Sie bleibt montiert, auch wenn `buehne` eine andere oder keine ist — versteckt, aber mit
   * lebenden Webviews, die die Fernsteuerung weiter erreicht.
   */
  angeheftet?: AngehefteteBuehne | null;
  /** Anheften/Loesen fuer die Buehne mit dieser Kennung. Fehlt er, gibt es keinen Knopf. */
  onAnheften?: (id: string, an: boolean) => void;
}

/**
 * Alle Reiter eines Fenster-Dokuments fuer den `state-report` einsammeln.
 * Ausgelagert, weil es zwei Meldewege gibt (WS-Verbindung und Arbeitsbereichs-
 * Wechsel) und beide dieselbe Menge melden muessen — sonst kennt der Server die
 * Buehnenkacheln mal und mal nicht, je nachdem, was zuletzt passiert ist.
 */
function sammleKacheln(
  m: Model | null,
  panels: Array<{ id: string; component: string; config: Record<string, unknown>; name: string }>,
): void {
  if (!m) return;
  m.visitNodes((node) => {
    if (node.getType() === 'tab') {
      const tab = node as TabNode;
      panels.push({ id: tab.getId(), component: tab.getComponent() ?? 'unknown', config: tab.getConfig() ?? {}, name: tab.getName() });
    }
  });
}

export default function LayoutManager({ projectId, layoutKey, workDir, cuiStates = {}, onAttentionChange, onCuiStateReset, pendingActivation, onActivationProcessed, isActive, onMissingSessions, buehne = null, buehnenBezug, angeheftet = null, onAnheften }: LayoutManagerProps) {
  // layoutId is the key used for layout persistence (localStorage + server API).
  // Separate from projectId so screen-2 windows use a different storage key
  // without affecting conversation sync, WS state-report, or active-dir logic.
  const layoutId = layoutKey ?? projectId;
  const { canAccessPanel, user } = useAuth();
  // Latest authenticated user id, captured in a ref so the long-lived WebSocket
  // onmessage closure can filter cross-user apply-layout broadcasts (layouts are
  // now stored per user server-side; another user's save must not be applied here).
  const myUserIdRef = useRef<string | null>(null);
  myUserIdRef.current = user?.id ?? null;

  // Toggle sync for a tool identified by component name (driven by Tool Hub pin button).
  // If the component already exists as a tab in this layout: unsync + remove from here + delete everywhere.
  // If the component does NOT exist: add it as a synced tab in this layout + push to all others.
  const toggleSyncTool = useCallback((component: string, displayName: string) => {
    const m = modelRef.current;
    if (!m) return;

    // Find an existing tab with this component
    let existingTab: TabNode | null = null;
    m.visitNodes((n) => {
      if (existingTab) return;
      if (n.getType() === 'tab') {
        const t = n as TabNode;
        if (t.getComponent() === component) existingTab = t;
      }
    });

    if (existingTab) {
      // Already mounted → user is un-pinning: remove from this layout AND from all others
      const existingTabId = (existingTab as TabNode).getId();
      try { m.doAction(Actions.deleteTab(existingTabId)); } catch { /* ignore */ }
      fetch(`${API}/layouts/delete-synced-tab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tabId: existingTabId }),
        signal: AbortSignal.timeout(5000),
      }).catch(err => console.warn('[LayoutManager] delete-synced-tab failed:', err));
      return;
    }

    // Not mounted → add synced tab to this layout + propagate to all others
    // Stable ID so all layouts reference the same tab (idempotent across sessions)
    const tabId = `#synced-${component}`;
    let targetTabsetId = '';
    m.visitNodes((n) => {
      if (!targetTabsetId && n.getType() === 'tabset') targetTabsetId = n.getId();
    });
    if (!targetTabsetId) return;

    const newConfig = { _synced: true };
    try {
      m.doAction(Actions.addNode(
        { type: 'tab', id: tabId, name: displayName, component, config: newConfig },
        targetTabsetId, DockLocation.CENTER, -1
      ));
    } catch (err) { console.warn('[LayoutManager] toggleSyncTool addNode failed:', err); return; }

    fetch(`${API}/layouts/sync-tab`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sourceProjectId: projectId,
        tabConfig: { id: tabId, name: displayName, component, config: newConfig },
      }),
      signal: AbortSignal.timeout(5000),
    }).catch(err => console.warn('[LayoutManager] sync-tab failed:', err));
  }, [projectId]);

  // Force every tab in the loaded layout to render eagerly. Persisted layouts
  // (server / localStorage cache) were written before tabEnableRenderOnDemand
  // was added to the default global config, so we patch the JSON before
  // Model.fromJson — otherwise background tabs stay unmounted and the
  // headless browser-panel API (eval/click/type/...) silently times out.
  const eagerifyLayoutJson = useCallback((json: IJsonModel): IJsonModel => {
    return {
      ...json,
      global: { ...(json.global ?? {}), tabEnableRenderOnDemand: false },
    };
  }, []);

  // Stale-while-revalidate: use cached layout instantly, refresh in background
  const [model, setModel] = useState<Model | null>(() => {
    try {
      const cached = localStorage.getItem(`cui-layout-${layoutId}`);
      if (cached) return Model.fromJson(eagerifyLayoutJson(JSON.parse(cached) as IJsonModel));
    } catch (err) { console.warn('[LayoutManager] Corrupted layout cache in initializer:', err); }
    return null;
  });
  /*
   * EIN LAYOUT, DAS SICH NICHT BAUEN LAESST, IST KEIN LADEVORGANG.
   *
   * Bis 08.09.2026 verschluckte `catch { console.warn(...) }` unten jeden
   * Bauversuch, der an einem kaputten Dokument scheiterte, und `attemptLoad`
   * meldete trotzdem 'resolved'. Ergebnis: `model` blieb null, es wurde nicht
   * erneut geholt, und die Flaeche stand fuer immer auf "Loading layout..." —
   * ohne einen einzigen Hinweis, woran es lag. Gemessen an devops.json, in das
   * am 07.09. 13:49Z eine blosse flexlayout-Zeile (`#guide-row`) statt eines
   * vollstaendigen Modells geschrieben worden war: `Model.fromJson` wirft dort,
   * weil der Schluessel `layout` fehlt. Rafael sah nur "Loading layout...".
   * Jetzt sagt die Flaeche, was kaputt ist (fail loud), statt ewig zu laden.
   */
  const [layoutDefekt, setLayoutDefekt] = useState<string | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [attentionVersion, setAttentionVersion] = useState(0); // triggers re-evaluation of attention state
  // Ephemeral per-tab state — kept out of the persisted Tab-Config to prevent save-loops.
  // _attention (idle/working/needs_attention) flips multiple times per second and used to be
  // written into Tab-Config via updateNodeConfig → triggered onModelChange → POST → Echo → Loop.
  // _route (live navigation in a CUI tab) had the same problem on every navigate.
  // Both are now memory-only refs; setAttentionVersion(v+1) re-renders Tab-Headers when changed.
  // Cost: navigation within a Tab is not persisted across browser reloads — Tab returns to initialSessionId.
  const attentionByNodeRef = useRef<Map<string, string>>(new Map());
  const routeByNodeRef = useRef<Map<string, string>>(new Map());
  const templateRef = useRef<IJsonModel | null>(null);
  const layoutRef = useRef<Layout>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const activeDirRef = useRef<string>(workDir);
  const controlWsRef = useRef<WebSocket | null>(null);

  // Per-session state tracking for tab indicators (updated via WS conv-attention events)
  // Key: sessionId, Value: { state, reason }
  const sessionStatesRef = useRef<Map<string, { state: string; reason?: string }>>(new Map());
  // Per-session pause tracking (updated via WS conv-paused events)
  const pausedSessionsRef = useRef<Set<string>>(new Set());
  // Force tab re-render counter (bumped when session states change)
  const [tabRenderTick, setTabRenderTick] = useState(0);

  // Refs for stable Layout callback props (prevent Layout re-render → revision++ → ALL tab content re-render)
  const cuiStatesRef = useRef(cuiStates);
  cuiStatesRef.current = cuiStates;
  const onCuiStateResetRef = useRef(onCuiStateReset);
  onCuiStateResetRef.current = onCuiStateReset;
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const onMissingSessionsRef = useRef(onMissingSessions);
  onMissingSessionsRef.current = onMissingSessions;
  const shareRoot = useRef<HTMLDivElement>(null);
  const modelRef = useRef<Model | null>(null);
  modelRef.current = model;
  /*
   * DIE BUEHNE IST EIN ZWEITES FENSTER-DOKUMENT — UND MUSS MITGEMELDET WERDEN.
   *
   * Die Kacheln einer Buehne leben in `buehnenModel` (siehe `buehne`-Prop weiter
   * unten), nicht im Arbeitsbereichs-Modell. Der `state-report` lief frueher nur
   * ueber `modelRef` — fuer den Server existierten Buehnenkacheln damit nicht, und
   * jedes Kommando (navigate/eval/click/screenshot) starb mit "not found in any
   * layout", obwohl die Kachel sichtbar dastand. Eine Buehne soll aber genau das
   * koennen, was ein Mensch im CUI von Hand kann (Rafael, 15.09.2026).
   * Gemeldet wird nur in den Arbeitsspeicher des Servers (layoutSnapshots) —
   * Layout-DATEIEN werden davon nicht angefasst.
   */
  const buehnenModelRef = useRef<Model | null>(null);
  // Das Fenstermodell der versteckt montierten, angehefteten Buehne — wird mitgemeldet,
  // damit der Server ihre Kacheln auch dann kennt, wenn sie gerade nicht gezeigt wird.
  const angeheftetModelRef = useRef<Model | null>(null);
  const meldeKachelnRef = useRef<(() => void) | null>(null);
  const modelInitialized = model !== null;  // stable boolean: changes only once (null->Model)

  // Version tracking for optimistic concurrency: prevents stale browser from overwriting
  // a newer server layout (set via API). Server increments _v on every write.
  const currentLayoutVersionRef = useRef<number>(0);
  // Suppress the next handleModelChange call after an external layout is applied
  // (WebSocket control:apply-layout or 409 recovery) to avoid echoing back to server.
  // Time-based suppression: when set, ignore handleModelChange until this epoch ms.
  // Boolean flag was insufficient because onModelChange fires multiple times per setModel
  // (React Re-Renders); the first call consumed the flag, the second triggered an echo save.
  const suppressUntilRef = useRef<number>(0);
  // Self-echo detection: when we POST a layout, store its JSON (sans _v). When the server
  // broadcasts our own write back to us, we recognize it and skip setModel — preventing
  // the re-mount cascade that re-loads every Chat panel from scratch.
  const lastSavedJsonRef = useRef<string>('');
  // Layout-sync race fixes:
  // (1) Tombstones: component -> epoch ms the user closed it. A colliding server
  //     layout must not resurrect a just-closed non-chat panel (e.g. Platform Admin).
  const closedComponentTombstonesRef = useRef<Map<string, number>>(new Map());
  // (1b) Session tombstones: sessionId -> epoch ms this client learned it was finished.
  // cui/cui-lite tabs are deliberately excluded from the component-tombstone above
  // (see the FlexLayout_DeleteTab handler) because the per-session conversation
  // auto-sync was assumed to govern them instead — but that cleanup only runs when
  // the user explicitly clicks Layout (window.__cuiAutoLayoutActive), NOT on the
  // control:apply-layout / 409-conflict fallback that resurrects tabs via setModel.
  // A stale layout blob (older snapshot, another window's queued save, a delayed
  // broadcast) can still carry a tab whose config.initialSessionId is a session the
  // user just finished — setModel remounts it, the panel broadcasts visibility, and
  // the server's auto-unfinish (state.ts updatePanelVisibility) treats that as a
  // genuine reopen once the 8s grace window has passed, silently un-finishing it.
  // Root-caused 2026-08-01: the "update" session (and this very admin session) each
  // got auto-unfinished twice in one day, always via a fresh tab with a NEW auto-
  // generated id — the signature of setModel recreating a resurrected tab.
  const finishedSessionTombstonesRef = useRef<Map<string, number>>(new Map());
  // (2) When the user just made an explicit layout change (Layout button / unstack /
  //     builder apply / reset), the LOCAL layout wins a concurrent 409 / apply-layout
  //     race instead of being overwritten by the server's older structure.
  const userLayoutActionUntilRef = useRef<number>(0);
  // Guards the single 409 re-POST retry so a persistently-conflicting server can't loop.
  const saveRetryGuardRef = useRef<boolean>(false);
  // Timestamp of the last navigate-request sent by the pendingActivation effect.
  // Blocks pruneEmptyCui during initial sync for 1500ms after activation to let the
  // CuiLitePanel round-trip (navigate-request → control:cui-navigate-conversation →
  // pollNow @+300ms → onRouteChange) complete before we prune panels with no route yet.
  // Bug: without this guard, the isActive 300ms sync fires while the route is still
  // unset, sees the chat panel as "empty", deletes it, and the session has no panel.
  const lastNavigateRequestSentAtRef = useRef<number>(0);
  // Self-cleaning tombstone check (drops expired entries on read).
  const isComponentTombstoned = useCallback((component: string): boolean => {
    const ts = closedComponentTombstonesRef.current.get(component);
    if (ts === undefined) return false;
    if (Date.now() - ts > TOMBSTONE_MS) {
      closedComponentTombstonesRef.current.delete(component);
      return false;
    }
    return true;
  }, []);
  const isSessionTombstoned = useCallback((sessionId: string): boolean => {
    const ts = finishedSessionTombstonesRef.current.get(sessionId);
    if (ts === undefined) return false;
    if (Date.now() - ts > SESSION_TOMBSTONE_MS) {
      finishedSessionTombstonesRef.current.delete(sessionId);
      return false;
    }
    return true;
  }, []);

  // Background refresh: fetch fresh layout from server (stale-while-revalidate)
  // Model is already loaded from localStorage cache in useState initializer above
  useEffect(() => {
    let cancelled = false;
    const cacheKey = `cui-layout-${layoutId}`;
    const tplCacheKey = `cui-template-${layoutId}`;
    const hadCachedModel = model !== null;

    // Initialize version from localStorage cache so we don't send a stale _v=0
    if (hadCachedModel) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (typeof parsed._v === 'number') {
            currentLayoutVersionRef.current = parsed._v;
          }
        }
      } catch { /* ignore */ }
    }

    // Load template cache if available
    try {
      const cachedTpl = localStorage.getItem(tplCacheKey);
      if (cachedTpl) templateRef.current = JSON.parse(cachedTpl);
    } catch (err) {
      console.warn('[LayoutManager] Corrupted template cache:', err);
      try { localStorage.removeItem(tplCacheKey); } catch (e) { console.warn('[LayoutManager] Failed to clear template cache:', e); }
    }

    // Fetch from server in background (update cache for next load)
    const fetchWithTimeout = (url: string, ms = 8000) =>
      fetch(url, { signal: AbortSignal.timeout(ms) }).then(r => r.ok ? r.json() : null).catch((err) => { console.warn('[LayoutManager] fetchWithTimeout failed for', url, ':', err); return null; });

    // Fail-fast contract: 404 vs "any other outcome" is load-bearing, NOT cosmetic.
    // 404 is the server's explicit confirmation "this project genuinely never had a
    // layout" — the only condition safe enough to seed a default (empty) layout.
    // Every other outcome (network error, timeout, 5xx, malformed body) means "we
    // don't actually know" and must leave the existing model untouched. Collapsing
    // both into one signal is what caused the 2026-08-04 incident: a transient fetch
    // failure during a cui-workspace.service restart was read as "no layout", a fresh
    // default was created, auto-synced sessions got mounted into it, and the empty
    // shell was auto-saved over the real hand-configured layout (Deploy/Browser panels
    // gone). Same differentiation the template fetch below already uses.
    const CONFIRMED_EMPTY = Symbol('confirmed-empty');
    const fetchLayout = (url: string, ms = 8000): Promise<(IJsonModel & { _v?: number }) | typeof CONFIRMED_EMPTY | null> =>
      fetch(url, { signal: AbortSignal.timeout(ms) })
        .then(async r => {
          if (r.status === 404) return CONFIRMED_EMPTY;
          if (!r.ok) { console.warn('[LayoutManager] layout fetch non-ok status', r.status, 'for', url); return null; }
          return await r.json();
        })
        .catch((err) => { console.warn('[LayoutManager] fetchLayout failed for', url, ':', err); return null; });

    // Template fetch differentiates "server says no template" from network error.
    // Server GET /layouts/:id/template returns 200 with null body when file is missing.
    // Sentinel { _deleted: true } means server confirmed no template → clear stale client cache.
    const fetchTemplate = (url: string, ms = 8000): Promise<IJsonModel | { _deleted: true } | null> =>
      fetch(url, { signal: AbortSignal.timeout(ms) })
        .then(async r => {
          if (r.status === 404) return { _deleted: true } as const;
          if (!r.ok) return null;
          const body = await r.json();
          return body === null ? { _deleted: true } as const : body;
        })
        .catch((err) => { console.warn('[LayoutManager] fetchTemplate failed for', url, ':', err); return null; });

    // Ein Bauversuch, dessen Scheitern nicht im Log verschwindet: gelingt er, ist
    // ein etwaiger frueherer Defekt geheilt; scheitert er, steht der Grund in der
    // Flaeche statt hinter einem ewigen "Loading layout...".
    const modellAnwenden = (json: IJsonModel): void => {
      try {
        setModel(Model.fromJson(eagerifyLayoutJson(json)));
        setLayoutDefekt(null);
      } catch (e) {
        const grund = e instanceof Error ? e.message : String(e);
        console.error('[LayoutManager] Layout-Dokument liess sich nicht bauen fuer', layoutId, ':', e);
        setLayoutDefekt(grund);
      }
    };

    // One fetch+apply cycle. Returns 'resolved' once we have a definitive answer
    // (real data or server-confirmed-empty) — the caller stops retrying. Returns
    // 'retry' for an unknown/failed outcome so the backoff loop below tries again
    // instead of leaving the window stuck on "Loading layout..." forever because of
    // what was ultimately just a slow server restart.
    const attemptLoad = async (): Promise<'resolved' | 'retry'> => {
      const [layoutJson, tplResult, activeDir] = await Promise.all([
        fetchLayout(`${API}/layouts/${layoutId}`),
        fetchTemplate(`${API}/layouts/${layoutId}/template`),
        fetchWithTimeout(`${API}/active-dir/${projectId}`),
      ]);
      if (cancelled) return 'resolved'; // unmounted — stop the loop, nothing left to apply
      if (activeDir?.path) activeDirRef.current = activeDir.path;
      if (tplResult && '_deleted' in tplResult) {
        // Server explicitly has no template — clear stale cache so reset uses defaultLayout
        templateRef.current = null;
        try { localStorage.removeItem(tplCacheKey); } catch (e) { console.warn('[LayoutManager] Failed to clear template cache:', e); }
      } else if (tplResult) {
        templateRef.current = tplResult as IJsonModel;
        try { localStorage.setItem(tplCacheKey, JSON.stringify(tplResult)); } catch (e) { console.warn('[LayoutManager] Failed to cache template:', e); }
      }
      if (layoutJson === CONFIRMED_EMPTY) {
        // Server confirmed: this project genuinely has no layout yet. Safe to seed a
        // default — there is nothing real to lose. Only applies with no local cache;
        // a cached model already answers "what was here" more accurately than a fresh
        // default would.
        if (!hadCachedModel) {
          try {
            setModel(Model.fromJson(eagerifyLayoutJson(defaultLayout(activeDirRef.current) as IJsonModel)));
            setLayoutDefekt(null);
          } catch (e) {
            console.error('[LayoutManager] Standard-Layout liess sich nicht bauen:', e);
            setLayoutDefekt(e instanceof Error ? e.message : String(e));
          }
        }
        return 'resolved';
      }
      if (!layoutJson) {
        // Fetch failed or returned something unusable — unknown state, NOT "empty".
        // Leave the model exactly as it is (null → still shows "Loading layout...";
        // cached → keep the cached one). Never fabricate — and never persist — a guess.
        return 'retry';
      }
      // Real layout body.
      try { localStorage.setItem(cacheKey, JSON.stringify(layoutJson)); } catch (e) { console.warn('[LayoutManager] Failed to cache layout:', e); }
      const serverV = typeof layoutJson._v === 'number' ? layoutJson._v : -1;
      if (serverV > currentLayoutVersionRef.current) {
        // Server has a newer version (e.g. set via API) — apply it and suppress echo.
        // Suppression must outlast handleModelChange's 1500ms debounce, otherwise the post-setModel
        // onChange wave fires saveLayout → 409 → re-mount loop with WS disconnect storm.
        currentLayoutVersionRef.current = serverV;
        suppressUntilRef.current = Date.now() + 2500;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        modellAnwenden(layoutJson as IJsonModel);
      } else if (!hadCachedModel) {
        // No local cache at all — apply whatever the server has
        if (serverV >= 0) currentLayoutVersionRef.current = serverV;
        modellAnwenden(layoutJson as IJsonModel);
      }
      return 'resolved';
    };

    if (hadCachedModel) {
      // A cached model is already showing — this is a background refresh, not the
      // user's only view. One attempt is enough; other triggers (ws-reconnect,
      // periodic sync, remount) will pick it up if this one happens to miss.
      void attemptLoad();
    } else {
      // Nothing to show yet — the user is looking at "Loading layout...". Retry with
      // backoff instead of giving up after one 8s timeout, so a server restart of a
      // few tens of seconds (exactly what a deploy causes) self-heals without ever
      // resorting to a fabricated default layout.
      const RETRY_DELAYS_MS = [0, 2000, 4000, 8000, 16000];
      (async () => {
        for (const delay of RETRY_DELAYS_MS) {
          if (cancelled) return;
          if (delay > 0) await new Promise(r => setTimeout(r, delay));
          if (cancelled) return;
          const outcome = await attemptLoad();
          if (outcome === 'resolved') return;
        }
        console.warn('[LayoutManager] layout fetch for', layoutId, 'still unresolved after retries — staying on "Loading layout..."');
      })();
    }

    return () => { cancelled = true; };
  }, [layoutId, workDir]);

  // Read ephemeral per-tab state. Falls back to Tab-Config so old persisted layouts
  // (with _attention/_route saved) still work during the transition.
  const getNodeAttention = useCallback((node: TabNode): string | undefined => {
    return attentionByNodeRef.current.get(node.getId()) ?? (node.getConfig()?._attention as string | undefined);
  }, []);
  const getNodeRoute = useCallback((node: TabNode): string | undefined => {
    return routeByNodeRef.current.get(node.getId()) ?? (node.getConfig()?._route as string | undefined);
  }, []);

  // Update a tab node's config and trigger debounced layout save
  const updateNodeConfig = useCallback((nodeId: string, patch: Record<string, string>) => {
    const m = modelRef.current;
    if (!m) return;
    try {
      const node = m.getNodeById(nodeId) as TabNode | null;
      if (!node) return;
      const existing = node.getConfig() ?? {};
      m.doAction(Actions.updateNodeAttributes(nodeId, { config: { ...existing, ...patch } }));
    } catch (err) { console.warn('[LayoutManager] updateNodeConfig failed for', nodeId, ':', err); }
  }, []);

  const factory = useCallback((node: TabNode) => {
    const component = node.getComponent();
    const config = node.getConfig() ?? {};
    const nodeId = node.getId();

    // Wrapper with data-node-id for screenshot targeting + ErrorBoundary for crash isolation
    // contain: strict limits layout recalculation scope when flexlayout measures via getBoundingClientRect
    const cleanNodeId = nodeId.replace(/^#/, '');
    // Re-applies the user's UI font-scale per panel (App.tsx resets it to 1 on
    // the workspace root so panels start from a clean base). BrowserPanel is
    // exempted — an Electron <webview> does not tolerate zoom/transform on any
    // ancestor (breaks its guest-viewport size calc, see BrowserPanel.tsx).
    const wrapPanel = (panelName: string, children: React.ReactNode) => (
      <div data-node-id={cleanNodeId} style={{ height: '100%', width: '100%', zoom: 'var(--cui-display-scale, 1)', display: 'flex', flexDirection: 'column', contain: 'strict' }}>
        <ErrorBoundary componentName={panelName}>
          {children}
        </ErrorBoundary>
      </div>
    );

    // Helper: wrap lazy-loaded components with Suspense
    const withSuspense = (children: React.ReactNode) => (
      <Suspense fallback={<PanelLoader />}>{children}</Suspense>
    );

    // Panel access check (no-op when auth is disabled)
    if (component && !canAccessPanel(component)) {
      return wrapPanel('AccessDenied',
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--tn-text-muted)', fontSize: 12, textAlign: 'center', padding: 20 }}>
          <div>
            <div style={{ fontSize: 18, marginBottom: 8, opacity: 0.5 }}>Restricted</div>
            <div>You don't have access to this panel.</div>
          </div>
        </div>
      );
    }

    switch (component) {
      case 'prod-chat':
        // Chat mit Produktions-Rechten. Workspace und Arbeitsverzeichnis setzt das
        // Panel selbst (PROD), die Freigabe verlangt es per Passwort.
        return wrapPanel('Prod Chat', <ProdChatPanel accountId={config.accountId} panelId={nodeId} isTabVisible={node.isVisible()}
          initialRoute={config._route}
          initialSessionId={config.initialSessionId}
          onRouteChange={(route) => { routeByNodeRef.current.set(nodeId, route); setAttentionVersion(v => v + 1); }}
          onStateChange={(state) => { attentionByNodeRef.current.set(nodeId, state); setAttentionVersion(v => v + 1); }}
          onFinish={(sid) => {
            const m = modelRef.current;
            if (m) {
              try { m.doAction(Actions.deleteTab(nodeId)); saveLayoutRef.current(m); } catch (e) { console.warn('[LM] Finish deleteTab:', e); }
            }
          }} />);
      case 'cui':
      case 'cui-lite':
        // FlexLayout visibility only describes the tab inside its model. Hidden
        // workspaces and the saved layout underneath a stage must not claim a
        // conversation and evict the visible stage chat.
        return wrapPanel('Chat', <CuiLitePanel accountId={config.accountId} projectId={projectId} workDir={workDir} panelId={nodeId}
          isTabVisible={isActive !== false && node.isVisible() && (!buehne || node.getModel() !== modelRef.current)}
          initialRoute={config._route}
          initialSessionId={config.initialSessionId}
          onRouteChange={(route) => { routeByNodeRef.current.set(nodeId, route); setAttentionVersion(v => v + 1); }}
          onStateChange={(state) => { attentionByNodeRef.current.set(nodeId, state); setAttentionVersion(v => v + 1); }}
          onFinish={(sid) => {
            const m = modelRef.current;
            if (m) {
              try { m.doAction(Actions.deleteTab(nodeId)); saveLayoutRef.current(m); } catch (e) { console.warn('[LM] Finish deleteTab:', e); }
            }
          }} />);
      case 'schnellbuehne':
        return <SchnellbuehnePanel html={typeof config.html === 'string' ? config.html : ''} />;
      case 'images':
        return wrapPanel('ImageDrop', <ImageDrop />);
      case 'browser': {
        // Auto-fill URL from workspace mapping when layout has no URL set.
        // Works for initial layout load (addTab() has same fallback for new tabs).
        const wsId = arbeitsbereichsKennung(workDir);
        const browserUrl = config.url || WORKSPACE_BROWSER_URLS[wsId] || '';
        return wrapPanel('BrowserPanel', <BrowserPanel initialUrl={browserUrl} panelId={nodeId}
          isolated={config.isolated === 'true' || (config.isolated as unknown) === true}
          partition={config.partition}
          konto={config.konto}
          ausfuellen={config.ausfuellen}
          onUrlChange={(url) => updateNodeConfig(nodeId, { url })} />);
      }
      case 'preview':
        // `datei` aus der Kachel-config: ein Rezept kann die Datei vorgeben, um die
        // es geht, statt nur den Ordner zu oeffnen.
      {
        // Buehnen-Kacheln (eigenes Modell) folgen ihrem Rezept, nie dem gemerkten Ordner.
        const imArbeitsbereich = node.getModel() === modelRef.current;
        const start = imArbeitsbereich
          ? startOrdner({
              konfigPfad: config.watchPath as string | undefined,
              grundwerte: [activeDirRef.current, workDir],
              gemerkt: liesStandardOrdner(myUserIdRef.current, workDir),
            })
          : (config.watchPath || activeDirRef.current || workDir);
        return wrapPanel('FilePreview', <FilePreview watchPath={start} initialFile={config.datei as string | undefined} stageDir={activeDirRef.current}
          haus={config.haus === 'dev' || config.haus === 'partner' ? config.haus : undefined}
          standardKey={imArbeitsbereich ? workDir : undefined}
          onWatchPathChange={(path) => updateNodeConfig(nodeId, { watchPath: path })} />);
      }
      case 'notes':
        return wrapPanel('NotesPanel', <NotesPanel projectId={projectId} />);
      case 'mission':
        return wrapPanel('MissionControl', withSuspense(<MissionControl projectId={config.projectId || projectId} workDir={config.workDir || workDir} />));
      case 'mission-chat':
        return wrapPanel('MissionChat', <CuiLitePanel accountId={config.accountId || 'engelmann'} projectId="mission-chat" workDir="/root/orchestrator/workspaces/mission-chat" panelId={nodeId} isTabVisible={node.isVisible()}
          initialSessionId={config.initialSessionId}
          onRouteChange={(route) => { routeByNodeRef.current.set(nodeId, route); setAttentionVersion(v => v + 1); }}
          onStateChange={(state) => { attentionByNodeRef.current.set(nodeId, state); setAttentionVersion(v => v + 1); }}
          onFinish={(sid) => {
            const m = modelRef.current;
            if (m) {
              try { m.doAction(Actions.deleteTab(nodeId)); saveLayoutRef.current(m); } catch (e) { console.warn('[LM] Finish deleteTab:', e); }
            }
          }} />);
      case 'gmail':
      case 'virtual-office':
        return wrapPanel('OfficePanel', withSuspense(<OfficePanel projectId={projectId} workDir={workDir} />));
      case 'knowledge':
      case 'knowledge-fullscreen':
        return wrapPanel('KnowledgeFullscreen', withSuspense(<KnowledgeFullscreen projectId={projectId} workDir={workDir} />));
      case 'platform-admin':
        return wrapPanel('PlatformAdmin', withSuspense(<PlatformAdmin />));
      case 'linkedin':
        return wrapPanel('LinkedInPanel',
          <PanelConnectivityGuard
            panelName="Platform"
            checkUrl="/api/panel-health"
            startCommand="# Platform managed by dev-servers CLI"
          >
            {withSuspense(<LinkedInPanel />)}
          </PanelConnectivityGuard>
        );
      case 'qa-dashboard':
        return wrapPanel('QADashboard', withSuspense(
          <QADashboard tab={config.tab} app={config.app} video={config.video} />
        ));
      case 'bridge-monitor':
        // No connectivity guard needed — all calls go through CUI server proxy
        return wrapPanel('BridgeMonitor', withSuspense(<BridgeMonitor />));
      case 'infisical-monitor':
        // Using mock data in development - no connectivity check needed
        return wrapPanel('InfisicalMonitor', withSuspense(<InfisicalMonitor />));
      case 'repo-dashboard':
        return wrapPanel('RepoDashboard', withSuspense(<RepoDashboard />));
      case 'watchdog':
      case 'infrastructure': // Alias: both point to Watchdog (iframe on :9090)
      case 'system-health': // Legacy alias: System Health is now a tab inside Watchdog
        return wrapPanel('WatchdogPanel', withSuspense(<WatchdogPanel />));
      case 'background-ops':
        return wrapPanel('BackgroundOps', withSuspense(<BackgroundOpsPanel />));
      case 'conversation-queue':
        return wrapPanel('ConversationQueue', withSuspense(<ConversationQueuePanel projectId={projectId} />));
      case 'maintenance':
        return wrapPanel('MaintenancePanel', withSuspense(<MaintenancePanel />));
      case 'input-audit':
        return wrapPanel('UserInputAuditPanel', withSuspense(<UserInputAuditPanel />));
      case 'architecture':
        return wrapPanel('ArchitectureExplorer', withSuspense(<ArchitectureExplorer />));
      case 'report-builder':
        return wrapPanel('ReportBuilder', withSuspense(<ReportBuilder />));
      case 'business-angel':
        return wrapPanel('Business Angel', withSuspense(<BusinessAngelPanel />));
      case 'privat-angel':
        return wrapPanel('Privat Angel', withSuspense(<PrivatAngelPanel />));
      case 'prompt-explorer':
        return wrapPanel('PromptExplorer', withSuspense(<PromptExplorer />));
      case 'my-tasks':
        return wrapPanel('MyTasks', withSuspense(<MyTasksPanel />));
      case 'focus':
        return wrapPanel('Fokus', withSuspense(<FocusPanel />));
      case 'energy-cases':
        return wrapPanel('Energy Cases', withSuspense(<EnergyCases />));
      case 'report-cases':
        return wrapPanel('Report Cases', withSuspense(<ReportCases />));
      case 'activity-feed':
        return wrapPanel('Activity Feed', withSuspense(<ActivityFeedPanel />));
      case 'partner-inbox':
        return wrapPanel('Partner Inbox', withSuspense(<PartnerInboxPanel projectId={projectId} />));
      case 'group-chat':
        return wrapPanel('Gruppenraum', withSuspense(<GroupChatPanel />));
      case 'deck-annotate':
        return wrapPanel('Businessplan-Deck', withSuspense(<DeckAnnotatePanel />));
      case 'report-annotate':
        // `reportId` aus der Kachel-config (Rezept: inhalt.reportId) — ohne sie sagt das Panel laut, was fehlt.
        return wrapPanel('Bericht-Anmerkungen', withSuspense(<ReportAnnotatePanel reportId={config.reportId as string | undefined} />));
      case 'customer-comms':
        return wrapPanel('Kundenkommunikation', withSuspense(<CustomerCommsPanel projectId={projectId} />));
      case 'netzwerk':
        return wrapPanel('Netzwerk', withSuspense(<NetzwerkPanel />));
      case 'feedback':
        return wrapPanel('Feedback', withSuspense(<FeedbackPanel />));
      case 'team-status':
        return wrapPanel('Team Status', withSuspense(<TeamStatusPanel />));
      case 'business-docs':
        // `docId` aus der Kachel-config: ein Rezept kann damit das Dokument
        // vorgeben, um das es geht, statt nur das Regal zu oeffnen.
        return wrapPanel('Business Docs', withSuspense(<BusinessDocsPanel initialDocId={config.docId as string | undefined} />));
      case 'business-navigator':
        // Business-Ordner lesen (Rafael 26.10.2026): Rezept-inhalt offen/filter/suche/hilfsordner/archivOffen.
        return wrapPanel('Business-Ordner', withSuspense(<BusinessNavigatorPanel config={config} />));
      case 'sales':
        return wrapPanel('Sales', withSuspense(<SalesPanel />));
      case 'business-overview':
        return wrapPanel('Business-Übersicht', withSuspense(<BusinessOverviewPanel />));
      case 'uploads':
        return wrapPanel('Uploads', withSuspense(<UploadPanel />));
      case 'calendar':
        return wrapPanel('Kalender', withSuspense(<CalendarPanel />));
      case 'mail':
        return wrapPanel('Mail', withSuspense(<MailPanel initialDraftId={typeof config.entwurf === 'string' ? config.entwurf : undefined} initialKonto={typeof config.konto === 'string' ? config.konto : undefined} initialTab={typeof config.tab === 'string' ? config.tab : undefined} />));
      case 'postfaecher':
        return wrapPanel('Postfächer', withSuspense(<PostfaecherPanel config={config} />));
      case 'tool-hub':
        // Der Tool Hub arbeitet AUF dem Arbeitsbereich. Ohne Pfad gibt es nichts zu zeigen,
        // und ein leerer String waere ein erfundener Ort — dann lieber der Satz, warum.
        if (!workDir) return wrapPanel('ToolHub', <div style={{ padding: 12, fontSize: 12, color: 'var(--tn-text-muted)' }}>
          Tool Hub braucht einen Arbeitsbereich. Diese Buehne ist ohne einen gebaut (Karte des anderen Hauses).
        </div>);
        return wrapPanel('ToolHub', withSuspense(<ToolHub projectId={projectId} workDir={workDir} />));
      case 'error-monitor':
        return wrapPanel('ErrorMonitor', withSuspense(<ErrorMonitor />));
      case 'deploy':
        return wrapPanel('Deploy', withSuspense(<DeployPanel initialApp={config.app as string | undefined} />));
      case 'partner-server':
        return wrapPanel('Partner Server Health', withSuspense(<PartnerServerPanel />));
      case 'partner-browsers':
        return wrapPanel('Partner-Browser', withSuspense(<PartnerBrowsersPanel />));
      case 'sandbox-privat-angel':
        return wrapPanel('Privat-Assistent', withSuspense(<SandboxAngelPanel mode="private" />));
      case 'sandbox-business-angel':
        return wrapPanel('Business-Assistent', withSuspense(<SandboxAngelPanel mode="business" />));
      case 'sandbox-rafael-angel':
        return wrapPanel('Rafael-Assistent', withSuspense(<SandboxAngelPanel mode="rafael" />));
      default:
        return wrapPanel(`Unknown:${component}`,
          <div style={{ padding: 20, color: 'var(--tn-text-muted)' }}>
            Unknown panel: {component}
          </div>
        );
    }
  }, [projectId, workDir, isActive, buehne]);

  // ============================================================================
  // mergeServerLayout — apply server's layout WITHOUT setModel when possible.
  //
  // Why: setModel(Model.fromJson(...)) re-mounts every panel because React sees a
  // new Model instance + new tab nodeIds → every CuiLite panel's WebSocket drops
  // and reconnects → "reload storm" the user sees.
  //
  // Race-condition that triggers this: multiple browser tabs on the same workspace
  // run independent syncConversations() loops every 30s. Both autonomously call
  // Actions.addNode(...) for the same conversation, each generating a different
  // random nodeId. POST → one wins, the other gets 409 → 409-handler runs setModel
  // with the winner's layout (different nodeIds for semantically identical tabs).
  //
  // Strategy: match tabs by SEMANTIC IDENTITY (component + key config field) rather
  // than nodeId. If both layouts have the same tab-set, just patch configs/names
  // via updateNodeAttributes (no re-mount). If tab-sets differ, fall back to
  // setModel (rare — only when panes were truly added/removed/restructured).
  // ============================================================================
  const tabIdentity = useCallback((tab: { component?: string; name?: string; config?: Record<string, unknown> }): string => {
    const cfg = (tab.config || {}) as Record<string, string | undefined>;
    if (cfg.initialSessionId) return `s|${cfg.initialSessionId}`;
    if (cfg.url) return `u|${cfg.url}`;
    if (cfg.watchPath) return `p|${cfg.watchPath}`;
    return `c|${tab.component || ''}|${tab.name || ''}`;
  }, []);

  // Walk JSON layout (server format) → flat list of {identity, name, component, config}
  const collectJsonTabs = useCallback((node: unknown, out: Array<{ identity: string; name: string; component: string; config: Record<string, unknown> }>): void => {
    if (!node || typeof node !== 'object') return;
    const n = node as { type?: string; component?: string; name?: string; config?: Record<string, unknown>; children?: unknown[] };
    if (n.type === 'tab') {
      out.push({
        identity: tabIdentity(n),
        name: n.name || '',
        component: n.component || '',
        config: n.config || {},
      });
      return;
    }
    for (const child of n.children || []) collectJsonTabs(child, out);
  }, [tabIdentity]);

  // Walk current Model → flat list of live tabs (with their actual nodeId for in-place updates)
  const collectModelTabs = useCallback((m: Model): Array<{ identity: string; nodeId: string; name: string; component: string; config: Record<string, unknown> }> => {
    const out: Array<{ identity: string; nodeId: string; name: string; component: string; config: Record<string, unknown> }> = [];
    m.visitNodes((node) => {
      if (node.getType() !== 'tab') return;
      const tab = node as TabNode;
      const config = (tab.getConfig?.() || {}) as Record<string, unknown>;
      out.push({
        identity: tabIdentity({ component: tab.getComponent(), name: tab.getName(), config }),
        nodeId: tab.getId(),
        name: tab.getName(),
        component: tab.getComponent() || '',
        config,
      });
    });
    return out;
  }, [tabIdentity]);

  // Try to merge server's layout into the current Model in-place.
  // Returns true if successful (no setModel needed). False → caller falls back to setModel.
  const tryMergeServerLayout = useCallback((m: Model | null, serverLayoutJson: { layout?: unknown } | null): boolean => {
    if (!m || !serverLayoutJson?.layout) return false;
    const serverTabs: Array<{ identity: string; name: string; component: string; config: Record<string, unknown> }> = [];
    collectJsonTabs(serverLayoutJson.layout, serverTabs);
    const localTabs = collectModelTabs(m);

    // If tab-sets differ in identity, the diff is too complex (panes added/removed/restructured)
    // → bail out, caller will setModel.
    const serverIds = new Set(serverTabs.map(t => t.identity));
    const localIds = new Set(localTabs.map(t => t.identity));
    if (serverIds.size !== serverTabs.length || localIds.size !== localTabs.length) return false; // duplicate identities — can't safely match
    if (serverIds.size !== localIds.size) return false;
    for (const id of serverIds) if (!localIds.has(id)) return false;

    // Same tab-set — patch any per-tab attributes that drifted (config / name)
    const localByIdentity = new Map(localTabs.map(t => [t.identity, t]));
    for (const sTab of serverTabs) {
      const lTab = localByIdentity.get(sTab.identity);
      if (!lTab) continue;
      const configDiffers = JSON.stringify(lTab.config) !== JSON.stringify(sTab.config);
      const nameDiffers = lTab.name !== sTab.name;
      if (!configDiffers && !nameDiffers) continue;
      try {
        const attrs: Record<string, unknown> = {};
        if (configDiffers) attrs.config = sTab.config;
        if (nameDiffers) attrs.name = sTab.name;
        m.doAction(Actions.updateNodeAttributes(lTab.nodeId, attrs));
      } catch (e) { console.warn('[LM merge] updateNodeAttributes failed for', lTab.nodeId, ':', e); }
    }
    return true;
  }, [collectJsonTabs, collectModelTabs]);

  const saveLayout = useCallback((m: Model) => {
    // Shadow client is a READ-ONLY layout mirror: it renders server layouts
    // for screenshots/browser-control but must never persist its own model.
    // Its headless rendering disagrees with the user's windows about what is
    // "empty"/visible, so its saves fight the user's over the shared
    // per-project layout — save→broadcast→apply ping-pong that visibly
    // rearranges the user's UI and swallows freshly opened panels.
    if (isShadowClient()) return;
    const json = m.toJson();
    const payload = { ...json, _v: currentLayoutVersionRef.current };
    // Remember what we just sent — apply-layout handler uses this to detect self-echo
    // (server broadcasts our own POST back to us, which would otherwise trigger setModel
    // and re-mount every CUI panel = the "chats reload every few seconds" problem)
    try { lastSavedJsonRef.current = JSON.stringify({ ...json }); } catch { lastSavedJsonRef.current = ''; }
    // Cache locally for instant load on next visit
    try { localStorage.setItem(`cui-layout-${layoutId}`, JSON.stringify(payload)); } catch (e) { console.warn('[LayoutManager] Failed to cache layout locally:', e); }
    if (window.__cuiServerAlive === false) return;
    try {
      fetch(`${API}/layouts/${layoutId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(15000),
      }).then(async (res) => {
        if (res.ok) {
          const data = await res.json().catch(() => ({})); // silent-ok: malformed layout save response; layout already persisted
          if (typeof data._v === 'number') currentLayoutVersionRef.current = data._v;
        } else if (res.status === 409) {
          // Server has newer version — apply it, suppress echo
          const data = await res.json().catch(() => ({})); // silent-ok: malformed 409 response; server-side layout version ignored
          if (data.layout) {
            currentLayoutVersionRef.current = typeof data._v === 'number' ? data._v : currentLayoutVersionRef.current;
            // Self-echo: if server's layout equals what we just sent, only bump version — don't setModel.
            // setModel would re-mount every child panel (WS disconnect storm), so skip when content is identical.
            let isSelfEcho = false;
            try {
              const { _v: _vIn, ...incomingNoV } = data.layout;
              if (lastSavedJsonRef.current && JSON.stringify(incomingNoV) === lastSavedJsonRef.current) {
                isSelfEcho = true;
              }
            } catch { /* compare failed; fall through to setModel */ }
            if (!isSelfEcho) {
              // User just made an explicit layout change (Layout button / unstack /
              // builder apply / reset): the LOCAL structure must win. Adopt the
              // server's version number and re-POST our layout once (loop-guarded)
              // instead of accepting the server's older arrangement. This is what
              // makes "click Layout" actually stick across a concurrent save.
              if (Date.now() < userLayoutActionUntilRef.current && !saveRetryGuardRef.current) {
                saveRetryGuardRef.current = true;
                setTimeout(() => { saveRetryGuardRef.current = false; }, 3000);
                logCuiTelemetry({ ts: Date.now(), kind: '409-local-wins-retry', trigger: 'server:409-conflict', projectId });
                const localM = modelRef.current;
                if (localM) saveLayoutRef.current?.(localM);
                return;
              }
              // Suppression must outlast handleModelChange's 1500ms debounce, otherwise the post-setModel
              // onChange wave fires another saveLayout → 409 → loop.
              suppressUntilRef.current = Date.now() + 2500;
              if (saveTimer.current) clearTimeout(saveTimer.current);
              // Try to merge in-place first (no re-mount). Only fall back to setModel if tab-sets differ.
              const merged = tryMergeServerLayout(modelRef.current, data.layout as { layout?: unknown });
              logCuiTelemetry({ ts: Date.now(), kind: '409-conflict-applied', trigger: 'server:409-conflict', projectId, merged });
              if (merged) {
                // Merge only patched attributes in-place on the LOCAL model. Persist
                // the LOCAL structure — NOT data.layout, which may differ structurally
                // (e.g. still-stacked tabs). Caching the server JSON here was the bug
                // that reverted the Layout-button unstack on the next reload.
                try {
                  const lm = modelRef.current;
                  if (lm) localStorage.setItem(`cui-layout-${layoutId}`, JSON.stringify({ ...lm.toJson(), _v: currentLayoutVersionRef.current }));
                } catch { /* ignore */ }
              } else {
                try {
                  // Don't resurrect a panel the user just closed (tombstone strip).
                  const safeLayout = stripTombstonedTabs(data.layout, isComponentTombstoned, isSessionTombstoned);
                  setModel(Model.fromJson(eagerifyLayoutJson(safeLayout as IJsonModel)));
                  localStorage.setItem(`cui-layout-${layoutId}`, JSON.stringify(safeLayout));
                } catch (e) { console.warn('[LayoutManager] Failed to apply conflict layout:', e); }
              }
            }
          }
        }
      }).catch((err) => { console.warn('[LayoutManager] saveLayout fetch failed:', err); });
    } catch (err) { console.warn('[LayoutManager] saveLayout error:', err); }
  }, [layoutId]);

  const handleModelChange = useCallback(
    (m: Model) => {
      // Time-based suppression covers the React Re-Render wave after setModel().
      // Self-echo of our own POSTs is handled separately in the apply-layout handler
      // (lastSavedJsonRef compare). Together: no save loops, no Chat re-mount cascades.
      if (Date.now() < suppressUntilRef.current) return;
      // We're past the echo-suppression window, so this is a genuine user-initiated layout
      // edit (resize/move). Defend it against a concurrent server layout for a short window
      // so the resulting save's 409 / apply-layout echo takes the loop-guarded "local wins →
      // re-POST local" path instead of replacing the user's visible arrangement with the
      // server's. See PASSIVE_LAYOUT_EDIT_MS.
      userLayoutActionUntilRef.current = Date.now() + PASSIVE_LAYOUT_EDIT_MS;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => saveLayout(m), 1500);
    },
    [saveLayout]
  );

  const saveTemplate = useCallback((tpl: IJsonModel) => {
    templateRef.current = tpl;
    if (window.__cuiServerAlive === false) return;
    try {
      fetch(`${API}/layouts/${layoutId}/template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tpl),
        signal: AbortSignal.timeout(15000),
      }).catch((err) => { console.warn('[LayoutManager] saveTemplate fetch failed:', err); });
    } catch (err) { console.warn('[LayoutManager] saveTemplate error:', err); }
  }, [layoutId]);

  const handleApplyLayout = useCallback((layoutJson: IJsonModel) => {
    try {
      userLayoutActionUntilRef.current = Date.now() + USER_LAYOUT_ACTION_MS;
      const newModel = Model.fromJson(eagerifyLayoutJson(layoutJson as IJsonModel));
      setModel(newModel);
      setShowBuilder(false);
      saveLayout(newModel);
      saveTemplate(layoutJson);
    } catch (err) {
      // Laut: ein Entwurf, der sich nicht bauen laesst, darf nicht wie ein
      // uebernommener aussehen — der Bauplan bleibt sonst wirkungslos stehen.
      console.error('[LayoutManager] handleApplyLayout: Layout liess sich nicht bauen:', err);
      setLayoutDefekt(err instanceof Error ? err.message : String(err));
    }
  }, [saveLayout, saveTemplate]);

  const handleResetLayout = useCallback(() => {
    try {
      // Reset means "back to the baked-in default", not "back to a user-saved template".
      // Drop the template cache so a stale per-workspace template can't hijack the default
      // (this happened when older layouts were saved without the /app-proxy browser URL).
      userLayoutActionUntilRef.current = Date.now() + USER_LAYOUT_ACTION_MS;
      templateRef.current = null;
      try { localStorage.removeItem(`cui-template-${layoutId}`); } catch (e) { console.warn('[LayoutManager] Failed to clear template cache on reset:', e); }
      const newModel = Model.fromJson(eagerifyLayoutJson(defaultLayout(workDir) as IJsonModel));
      setModel(newModel);
      saveLayout(newModel);
    } catch (err) {
      console.error('[LayoutManager] handleResetLayout: Standard-Layout liess sich nicht bauen:', err);
      setLayoutDefekt(err instanceof Error ? err.message : String(err));
    }
  }, [workDir, layoutId, saveLayout]);

  // Stable refs for WS effect (prevent reconnect on model/callback changes)
  const handleResetLayoutRef = useRef(handleResetLayout);
  handleResetLayoutRef.current = handleResetLayout;
  const saveLayoutRef = useRef(saveLayout);
  saveLayoutRef.current = saveLayout;

  const addTab = useCallback((type: 'cui' | 'cui-lite' | 'browser' | 'preview' | 'notes' | 'images' | 'mission' | 'gmail' | 'platform-admin' | 'linkedin' | 'system-health' | 'bridge-monitor' | 'repo-dashboard' | 'watchdog' | 'background-ops' | 'conversation-queue' | 'maintenance' | 'input-audit' | 'qa-dashboard' | 'peer-awareness' | 'infisical-monitor' | 'mission-chat' | 'architecture' | 'report-builder' | 'prompt-explorer' | 'sub-sessions' | 'business-docs' | 'sales' | 'business-overview' | 'my-tasks' | 'activity-feed' | 'partner-inbox' | 'customer-comms' | 'netzwerk' | 'feedback' | 'team-status' | 'uploads', config: Record<string, string>, targetId: string) => {
    const m = modelRef.current;
    if (!m) return;
    // Panel-Namen kommen aus der Registry (SSoT)
    if (type === 'preview' && !config.watchPath) {
      config.watchPath = liesStandardOrdner(myUserIdRef.current, workDir) || activeDirRef.current || workDir || '';
    }
    if (type === "browser" && !config.url) {
      const wsId = arbeitsbereichsKennung(workDir);
      if (WORKSPACE_BROWSER_URLS[wsId]) config.url = WORKSPACE_BROWSER_URLS[wsId];
    }
    try {
      m.doAction(
        Actions.addNode(
          { type: 'tab', name: PANEL_NAMES[type] ?? type, component: type, config },
          targetId,
          DockLocation.CENTER,
          -1
        )
      );
    } catch (err) { console.warn('[LayoutManager] addTab doAction failed for', type, ':', err); }
  }, [workDir]);

  const onRenderTabSet = useCallback((node: TabSetNode | BorderNode, renderValues: ITabSetRenderValues) => {
    if (node instanceof BorderNode) return;
    const nodeId = node.getId().replace(/^#/, '');

    // Add-tab dropdown
    renderValues.stickyButtons.push(
      <select
        key="add-tab"
        data-ai-id={`add-tab-dropdown-${nodeId}`}
        value=""
        onChange={(e) => {
          const val = e.target.value;
          if (!val) return;
          if (val === 'cui') {
            addTab('cui', { _userReserved: String(Date.now()) }, node.getId());
          } else {
            addTab(val as 'browser' | 'preview' | 'notes' | 'images' | 'mission' | 'gmail' | 'platform-admin' | 'linkedin' | 'system-health' | 'bridge-monitor' | 'repo-dashboard' | 'watchdog' | 'background-ops' | 'conversation-queue' | 'maintenance' | 'input-audit' | 'qa-dashboard' | 'peer-awareness' | 'infisical-monitor' | 'mission-chat' | 'architecture' | 'report-builder' | 'prompt-explorer' | 'sub-sessions' | 'business-docs' | 'sales' | 'business-overview' | 'my-tasks' | 'activity-feed' | 'partner-inbox' | 'customer-comms' | 'netzwerk' | 'feedback' | 'team-status' | 'uploads', {}, node.getId());
          }
          e.target.value = '';
        }}
        title="Tab hinzufuegen"
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--tn-text-muted)',
          fontSize: 14,
          cursor: 'pointer',
          padding: '0 2px',
          width: 20,
          appearance: 'none',
          WebkitAppearance: 'none',
        }}
      >
        <option value="">+</option>
        {menuGroupsForRole(user?.role).map(({ category, items }) =>
          items.length === 0 ? null : (
            <optgroup key={category} label={category}>
              {items.map(({ value, label }) => {
                const allowed = canAccessPanel(value);
                return (
                  <option key={value} value={value} disabled={!allowed}
                    style={!allowed ? { color: 'var(--tn-text-muted)', opacity: 0.5 } : undefined}>
                    {allowed ? label : `${label} (nicht verfügbar)`}
                  </option>
                );
              })}
            </optgroup>
          )
        )}
      </select>
    );
  }, [addTab, canAccessPanel, user?.role]);

  // When cuiStates changes, update tab header dots via DOM (no React re-render needed).
  // Direct DOM manipulation avoids triggering flexlayout's expensive render/layout cycle.
  useEffect(() => {
    if (!model) return;
    model.visitNodes((node) => {
      if (node.getType() !== 'tab') return;
      const tab = node as TabNode;
      if (tab.getComponent() !== 'cui') return;
      const cuiId = tab.getConfig()?.accountId;
      if (!cuiId) return;
      const state = cuiStates[cuiId];
      // Find the tab button element by flexlayout's data attribute
      const tabEl = document.querySelector(`[data-layout-path="${tab.getId()}"]`)
        ?? document.querySelector(`.flexlayout__tab_button[data-node="${tab.getId()}"]`);
      if (!tabEl) return;
      let dot = tabEl.querySelector('.cui-state-dot') as HTMLElement;
      if (state === 'processing' || state === 'done') {
        if (!dot) {
          dot = document.createElement('span');
          dot.className = 'cui-state-dot';
          Object.assign(dot.style, { width: '7px', height: '7px', borderRadius: '50%', display: 'inline-block', marginRight: '4px', flexShrink: '0' });
          tabEl.insertBefore(dot, tabEl.firstChild);
        }
        dot.style.background = state === 'processing' ? 'var(--tn-green)' : 'var(--tn-orange)';
      } else if (dot) {
        dot.remove();
      }
    });
  }, [model, cuiStates]);

  // Reset CUI state to idle when user selects a CUI tab
  const handleAction = useCallback((action: Action) => {
    const m = modelRef.current;
    if (action.type === 'FlexLayout_SelectTab' && m) {
      const nodeId = action.data?.tabNode;
      if (nodeId) {
        try {
          const node = m.getNodeById(nodeId);
          if (node && (node as TabNode).getComponent?.() === 'cui') {
            const cuiId = (node as TabNode).getConfig?.()?.accountId;
            if (cuiId && cuiStatesRef.current[cuiId] === 'done') {
              onCuiStateResetRef.current?.(cuiId);
            }
          }
        } catch (err) { console.warn('[LayoutManager] handleAction tab lookup failed:', err); }
      }
    }
    // Intercept tab close: if synced, remove from all other layouts too
    if (action.type === 'FlexLayout_DeleteTab' && m) {
      const nodeId = action.data?.tabNode;
      if (nodeId) {
        try {
          const node = m.getNodeById(nodeId);
          // Record close-intent for non-chat panels so a colliding server layout
          // re-apply can't resurrect them. cui/cui-lite are governed by the
          // conversation auto-sync instead — never tombstone those.
          const closedComp = node ? (node as TabNode).getComponent?.() : '';
          if (closedComp && closedComp !== 'cui' && closedComp !== 'cui-lite') {
            closedComponentTombstonesRef.current.set(closedComp, Date.now());
          }
          if (node && (node as TabNode).getConfig?.()._synced) {
            fetch(`${API}/layouts/delete-synced-tab`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ tabId: nodeId }),
              signal: AbortSignal.timeout(5000),
            }).catch(err => console.warn('[LayoutManager] delete-synced-tab failed:', err));
          }
        } catch (err) { console.warn('[LayoutManager] delete-synced-tab check failed:', err); }
      }
    }
    return action;
  }, []);

  const onRenderTab = useCallback((node: TabNode, renderValues: ITabRenderValues) => {
    // Show node ID badge on every tab — clickable to copy full ID
    const fullId = node.getId().replace(/^#/, '');
    const shortId = fullId.slice(0, 6);
    renderValues.buttons.push(
      <span
        key="node-id"
        title={`Click to copy: ${fullId}`}
        onClick={(e) => {
          e.stopPropagation();
          copyToClipboard(fullId);
          const el = e.currentTarget;
          el.textContent = 'copied!';
          el.style.color = 'var(--tn-green)';
          setTimeout(() => { el.textContent = shortId; el.style.color = 'var(--tn-text-muted)'; }, 1200);
        }}
        style={{
          fontSize: 9, color: 'var(--tn-text-muted)', opacity: 0.7,
          fontFamily: 'monospace', marginLeft: 6, cursor: 'pointer',
          padding: '1px 4px', borderRadius: 3,
          background: 'var(--tn-surface-alt)',
        }}
      >{shortId}</span>
    );

    // Screenshot button — captures panel content, returns server path
    renderValues.buttons.push(
      <span
        key="screenshot"
        title="Screenshot vom Panel"
        onClick={async (e) => {
          e.stopPropagation();
          const btn = e.currentTarget;
          const original = btn.textContent;
          const setLabel = (text: string, color: string) => {
            btn.textContent = text;
            btn.style.color = color;
          };
          setLabel('⏳', 'var(--tn-yellow)');
          try {
            const target = document.querySelector<HTMLElement>(`[data-node-id="${fullId}"]`);
            if (!target) throw new Error(`Panel ${shortId} nicht im DOM`);
            const rect = target.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) throw new Error('Panel ist nicht sichtbar');

            // A browser panel renders an Electron <webview> in a separate
            // web-contents — html2canvas cannot see into it. Use the webview's
            // native capturePage() for the real page pixels; html2canvas stays
            // for ordinary DOM panels.
            let dataUrl: string;
            let shotW: number;
            let shotH: number;
            const wvs = target.querySelectorAll('webview');
            const webview = wvs.length === 1 ? (wvs[0] as any) : null;
            // Browser-Panel im normalen Web-Browser (nicht die CUI-App): die Seite steckt
            // in einem <iframe> einer ANDEREN Herkunft (z. B. 5003.partner.werking.tools).
            // html2canvas darf da nicht hineinsehen — das Ergebnis war Fensterleiste +
            // weisse Flaeche, und durch das Aufklappen unten schrumpfte der Rahmen auf
            // seine 150-px-Grundhoehe (Meldung patrick-heidecker, 14.10.2026: 1740x180).
            // Der einzige Weg an die echten Pixel ist die Bildschirmfreigabe dieses Tabs;
            // der Browser fragt dafuer bei jedem Klick nach. getDisplayMedia MUSS vor dem
            // ersten await laufen, sonst ist die Klick-Berechtigung verbraucht.
            const iframes = target.querySelectorAll('iframe');
            const iframeImBrowser = !window.electronAPI?.isElectron && iframes.length === 1;
            if (webview && window.electronAPI?.isElectron && typeof webview.capturePage === 'function') {
              const image = await webview.capturePage();
              dataUrl = image.toDataURL();
              const sz = image.getSize();
              shotW = sz.width;
              shotH = sz.height;
            } else if (iframeImBrowser) {
              if (!navigator.mediaDevices?.getDisplayMedia) {
                throw new Error('Dieser Browser kann den Tab nicht aufnehmen (getDisplayMedia fehlt) — Chrome/Edge verwenden oder die CUI-App');
              }
              const stream = await navigator.mediaDevices.getDisplayMedia({
                video: { displaySurface: 'browser' } as MediaTrackConstraints,
                audio: false,
                preferCurrentTab: true,
                selfBrowserSurface: 'include',
              } as DisplayMediaStreamOptions);
              try {
                const video = document.createElement('video');
                video.muted = true;
                video.srcObject = stream;
                await video.play();
                // Die Freigabeleiste des Browsers kann das Fenster kurz umbrechen lassen.
                await new Promise(r => setTimeout(r, 400));
                const settings = stream.getVideoTracks()[0]?.getSettings() as { displaySurface?: string } | undefined;
                if (settings?.displaySurface && settings.displaySurface !== 'browser') {
                  throw new Error('Bitte DIESEN Browser-Tab freigeben, nicht Fenster oder Bildschirm');
                }
                const r2 = target.getBoundingClientRect();
                const sx = video.videoWidth / window.innerWidth;
                const sy = video.videoHeight / window.innerHeight;
                if (!video.videoWidth || !video.videoHeight) throw new Error('Tab-Aufnahme lieferte kein Bild');
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(r2.width * sx);
                canvas.height = Math.round(r2.height * sy);
                const ctx = canvas.getContext('2d');
                if (!ctx) throw new Error('Canvas nicht verfuegbar');
                ctx.drawImage(video, r2.left * sx, r2.top * sy, r2.width * sx, r2.height * sy, 0, 0, canvas.width, canvas.height);
                dataUrl = canvas.toDataURL('image/png');
                shotW = canvas.width;
                shotH = canvas.height;
              } finally {
                for (const t of stream.getTracks()) t.stop();
              }
            } else {
              // Temporarily expand the panel + all scrollable descendants so
              // html2canvas captures the full content, not just the viewport.
              // Panel briefly grows + restores — acceptable for manual action.
              type Saved = { el: HTMLElement; scrollTop: number; scrollLeft: number; cssText: string };
              const saved: Saved[] = [];
              const expand = (el: HTMLElement, isTarget: boolean) => {
                saved.push({ el, scrollTop: el.scrollTop, scrollLeft: el.scrollLeft, cssText: el.style.cssText });
                el.style.overflow = 'visible';
                el.style.overflowX = 'visible';
                el.style.overflowY = 'visible';
                el.style.maxHeight = 'none';
                if (isTarget) {
                  el.style.contain = 'none';
                  el.style.height = 'auto';
                } else {
                  el.style.height = 'auto';
                  el.style.maxWidth = 'none';
                }
                el.scrollTop = 0;
                el.scrollLeft = 0;
              };
              expand(target, true);
              for (const child of Array.from(target.querySelectorAll<HTMLElement>('*'))) {
                const cs = getComputedStyle(child);
                const ov = `${cs.overflow}${cs.overflowX}${cs.overflowY}`;
                if (ov.includes('auto') || ov.includes('scroll')) expand(child, false);
              }
              await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));

              const fullW = target.scrollWidth;
              const fullH = target.scrollHeight;
              const html2canvas = (await import('html2canvas')).default;
              let canvas: HTMLCanvasElement;
              try {
                canvas = await html2canvas(target, {
                  backgroundColor: aufnahmeHintergrund(), onclone: farbenFuerHtml2canvas, scale: 1, useCORS: true, logging: false, allowTaint: true,
                  width: fullW, height: fullH, windowWidth: fullW, windowHeight: fullH,
                });
              } finally {
                for (const s of saved) {
                  s.el.style.cssText = s.cssText;
                  s.el.scrollTop = s.scrollTop;
                  s.el.scrollLeft = s.scrollLeft;
                }
              }
              dataUrl = canvas.toDataURL('image/png');
              shotW = canvas.width;
              shotH = canvas.height;
            }
            const resp = await fetch(`/api/screenshot/${encodeURIComponent(fullId)}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              // fuerSitzung: eigene Datei je Klick, dort abgelegt, wo die Claude-Sitzung
              // (auch eine isolierte Partner-Sitzung) sie lesen kann.
              body: JSON.stringify({ dataUrl, width: shotW, height: shotH, fuerSitzung: true }), // trust-boundary:ok — Route hat kein Schema, prueft dataUrl selbst (400)
              signal: AbortSignal.timeout(20000),
            });
            if (!resp.ok) {
              const body = await resp.json().catch(() => null) as { error?: string } | null;
              throw new Error(`Server ${resp.status}${body?.error ? `: ${body.error}` : ''}`);
            }
            const data = await resp.json() as { filePath?: string; url?: string; sitzungsPfad?: string };
            const pathToCopy = data.sitzungsPfad
              ? `Schau dir dieses Bild an: ${data.sitzungsPfad}`
              : (data.filePath || data.url || '');
            await copyToClipboard(pathToCopy);
            setLabel('✓ kopiert', 'var(--tn-green)');
            console.log(`[Screenshot] ${shortId}: ${pathToCopy}`);
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setLabel('✗', 'var(--tn-red)');
            // Den Grund sichtbar machen (Tooltip), nicht nur in der Konsole.
            btn.title = `Screenshot fehlgeschlagen: ${msg}`;
            console.error(`[Screenshot] ${shortId}:`, msg);
          }
          setTimeout(() => setLabel(original || '📷', 'var(--tn-text-muted)'), 1800);
        }}
        style={{
          fontSize: 11, color: 'var(--tn-text-muted)', opacity: 0.7,
          marginLeft: 4, cursor: 'pointer',
          padding: '1px 4px', borderRadius: 3,
          background: 'var(--tn-surface-alt)',
        }}
      >📷</span>
    );

    // Sync state is controlled from Tool Hub (not tab header)
    const tabComp = node.getComponent();
    if (tabComp !== 'cui' && tabComp !== 'cui-lite' && tabComp !== 'mission-chat') {
      return;
    }

    // Primary: panel-reported attention state (from CuiLitePanel onStateChange callback)
    const panelState = getNodeAttention(node);

    // Fallback: per-session WS state or per-account legacy state
    let sessionState = panelState || 'idle';
    let backgroundPending = false; // idle + reason 'background': turn ended, background work still running
    if (sessionState === 'idle') {
      const route = getNodeRoute(node);
      const sessionId = route?.startsWith('/c/') ? route.slice(3) : null;
      if (sessionId) {
        const ss = sessionStatesRef.current.get(sessionId);
        if (ss && ss.state !== 'idle') sessionState = ss.state;
        else if (ss && ss.state === 'idle' && ss.reason === 'background') backgroundPending = true;
      }
    }

    // State indicators: working (green pulse), needs_attention (red pulse), paused (grey), idle (dim)
    const sessionId = node.getConfig()?.initialSessionId || node.getConfig()?.sessionId;
    const isPaused = sessionId ? pausedSessionsRef.current.has(sessionId) : false;
    if (isPaused) {
      renderValues.leading = <span key="dot" className="cui-tab-dot cui-tab-dot--paused" title="Paused">⏸</span>;
    } else if (sessionState === 'working') {
      renderValues.leading = <span key="dot" className="cui-tab-dot cui-tab-dot--working" />;
    } else if (sessionState === 'needs_attention') {
      const attentionReason = node.getConfig()?._attentionReason;
      const label = attentionReason === 'permission' ? '⚡' : attentionReason === 'error' ? '⚠' : '●';
      renderValues.leading = <span key="dot" className="cui-tab-dot cui-tab-dot--attention" title={attentionReason || 'Needs input'}>{label}</span>;
    } else if (backgroundPending) {
      renderValues.leading = <span key="dot" className="cui-tab-dot cui-tab-dot--waiting" title="Hintergrundlauf aktiv — meldet sich selbst" />;
    } else if (getNodeRoute(node)) {
      // Has a conversation open but idle
      renderValues.leading = <span key="dot" className="cui-tab-dot cui-tab-dot--idle" />;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabRenderTick, getNodeAttention, getNodeRoute]);

  // Stable refs for Layout callbacks — prevent Layout element recreation on state changes.
  // Without these, every tabRenderTick bump recreates the <Layout> element via useMemo,
  // which causes flexlayout to re-render ALL visible tabs (expensive!).
  const onRenderTabStableRef = useRef(onRenderTab);
  onRenderTabStableRef.current = onRenderTab;
  const stableOnRenderTab = useCallback((node: TabNode, rv: ITabRenderValues) => {
    onRenderTabStableRef.current(node, rv);
  }, []);

  const onRenderTabSetStableRef = useRef(onRenderTabSet);
  onRenderTabSetStableRef.current = onRenderTabSet;
  const stableOnRenderTabSet = useCallback((node: TabSetNode | BorderNode, rv: ITabSetRenderValues) => {
    onRenderTabSetStableRef.current(node, rv);
  }, []);

  // Control API: listen for panel/layout commands + report panel state (auto-reconnect)
  // IMPORTANT: No model/callback dependencies — uses refs to prevent WS reconnect on every model change
  useEffect(() => {
    let disposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoff = 1000; // start at 1s, doubles up to 30s max

    const connect = () => {
      if (disposed) return;
      if (window.__cuiServerAlive === false) {
        reconnectTimer = setTimeout(connect, Math.min(backoff, 10000));
        return;
      }
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      // Nebenkanal, kein Fenster: nicht Ziel der Fensterabfrage (server/lib/guide-fenster-abfrage.ts).
      const ws = new WebSocket(`${protocol}://${window.location.host}/ws?kanal=neben`);
      ws.onerror = () => {};
      controlWsRef.current = ws;

      function reportPanels() {
        const m = modelRef.current;
        if (!m || ws.readyState !== WebSocket.OPEN) return;
        try {
          const panels: Array<{ id: string; component: string; config: Record<string, unknown>; name: string }> = [];
          sammleKacheln(m, panels);
          sammleKacheln(buehnenModelRef.current, panels);
          sammleKacheln(angeheftetModelRef.current, panels);
          const msg: Record<string, unknown> = { type: 'state-report', panels, projectId };
          if (isActiveRef.current) msg.activeProjectId = projectId;
          ws.send(JSON.stringify(msg));
        } catch (err) { console.warn('[LayoutManager] reportPanels failed:', err); }
      }

      meldeKachelnRef.current = reportPanels;

      ws.onopen = () => {
        backoff = 1000;
        reportPanels();
        // Re-sync conversations on reconnect
        setTimeout(() => syncNowRef.current?.('ws-reconnect'), 1500);
        // Auto-layout disabled on reconnect — only triggered manually via Layout button
      };
      ws.onclose = () => {
        if (controlWsRef.current === ws) controlWsRef.current = null;
        if (!disposed) {
          reconnectTimer = setTimeout(() => {
            backoff = Math.min(backoff * 2, 30000);
            connect();
          }, backoff);
        }
      };
      ws.onmessage = (e) => {
      const m = modelRef.current;
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'control:panel-add' && m) {
          // Ziel-Projekt-Guard (deterministisch, KEIN Fallback): der Broadcast
          // erreicht JEDE gemountete LayoutManager-Instanz (auch im Hintergrund
          // gemountete Fremd-Projekte). Ein Panel wird NUR in der Instanz
          // eingefügt, die das explizit benannte Ziel-Projekt rendert. Fehlt
          // msg.projectId, ist NIEMAND zuständig (fail-closed, laut) — es wird
          // NIE über "aktives Projekt" geraten. Genau dieses Raten streute
          // Panels in fremde Workspaces (2026-07-23) und ließ, vor einem
          // veralteten Renderer-Bundle, alle gemounteten Instanzen zugleich
          // zuschlagen. Der Server erzwingt projectId (400) — dies ist die
          // zweite Verteidigungslinie im Client.
          const targetProject = typeof msg.projectId === 'string' ? msg.projectId.trim() : '';
          if (!targetProject) {
            console.warn('[LayoutManager] panel-add OHNE projectId ignoriert — kein aktives-Projekt-Fallback');
          } else if (targetProject === projectId) {
            let targetId = '';
            m.visitNodes((node) => { if (!targetId && node.getType() === 'tabset') targetId = node.getId(); });
            if (targetId) {
              try {
                m.doAction(Actions.addNode(
                  { type: 'tab', name: msg.name || msg.component, component: msg.component, config: msg.config || {} },
                  targetId, DockLocation.CENTER, -1
                ));
                reportPanels();
              } catch (err) { console.warn('[LayoutManager] panel-add doAction failed:', err); }
            }
          }
        }
        if (msg.type === 'control:panel-remove' && msg.nodeId && m) {
          try {
            m.doAction(Actions.deleteTab(msg.nodeId));
            reportPanels();
          } catch (err) { console.warn('[LayoutManager] panel-remove doAction failed:', err); }
        }
        if ((msg.type === 'control:conversation-finished' || msg.type === 'control:conversation-deleted') && m) {
          // Only close panels the server explicitly lists — no aggressive search by sessionId
          // (aggressive deletion caused panels to disappear on any auto-finish event)
          const myPanels = ((msg.panelsToClose || []) as Array<{ panelId: string; projectId: string }>)
            .filter(p => p.projectId === projectId);
          let closed = 0;
          for (const p of myPanels) {
            try { m.doAction(Actions.deleteTab(p.panelId)); closed++; } catch (err) { console.warn('[LayoutManager] deleteTab failed for panel', p.panelId, ':', err); }
          }
          if (closed > 0) {
            reportPanels();
            saveLayoutRef.current(m);
          }
          // Tombstone the session itself (not just the panel) — see finishedSessionTombstonesRef
          // above. Without this, a stale layout snapshot applied via setModel (409-conflict or
          // control:apply-layout fallback) can resurrect this exact session into a brand-new tab.
          if (typeof msg.sessionId === 'string' && msg.sessionId) {
            finishedSessionTombstonesRef.current.set(msg.sessionId, Date.now());
          }
        }
        if (msg.type === 'control:conversation-started' && msg.workDir === workDir) {
          // New conversation for our project — short delay to let session initialize
          setTimeout(() => syncNowRef.current?.('ws:conversation-started'), 1000);
        }
        if (msg.type === 'control:layout-reset') {
          handleResetLayoutRef.current();
          setTimeout(reportPanels, 200);
        }
        // Nuclear option: clear all layout caches and force full reload from server
        if (msg.type === 'control:nuke-layout-cache') {
          try {
            const keys = Object.keys(localStorage).filter(k => k.startsWith('cui-layout-') || k.startsWith('cui-template-') || k.startsWith('cui-msgs-'));
            keys.forEach(k => localStorage.removeItem(k));
            console.log(`[LayoutManager] Nuked ${keys.length} layout cache entries, reloading...`);
          } catch (e) { console.warn('[LayoutManager] nuke-layout-cache error:', e); }
          setTimeout(() => window.location.reload(), 500);
        }
        // Notify in-process listeners (Tool Hub, etc.) that layout/sync state changed
        if (msg.type === 'synced-tab-added' || msg.type === 'synced-tab-removed' || msg.type === 'control:apply-layout') {
          try { window.dispatchEvent(new CustomEvent('cui-layout-changed', { detail: { projectId: msg.projectId || projectId } })); } catch { /* ignore */ }
        }
        // Synced tab added in another workspace → add to our layout if we're affected
        if (msg.type === 'synced-tab-added' && msg.tabConfig && Array.isArray(msg.affectedProjectIds) && msg.affectedProjectIds.includes(projectId)) {
          const tc = msg.tabConfig;
          if (m) {
            let alreadyPresent = false;
            m.visitNodes((n) => { if (n.getId() === tc.id) alreadyPresent = true; });
            if (!alreadyPresent) {
              let rightmostTabsetId = '';
              m.visitNodes((n) => { if (n.getType() === 'tabset') rightmostTabsetId = n.getId(); });
              if (rightmostTabsetId) {
                try {
                  m.doAction(Actions.addNode(
                    { type: 'tab', id: tc.id, name: tc.name, component: tc.component, config: tc.config },
                    rightmostTabsetId, DockLocation.CENTER, -1
                  ));
                  saveLayoutRef.current(m);
                } catch (err) { console.warn('[LayoutManager] synced-tab-added addNode failed:', err); }
              }
            }
          }
        }
        // Synced tab removed in another workspace → remove from our layout
        if (msg.type === 'synced-tab-removed' && msg.tabId && m) {
          let found = false;
          m.visitNodes((n) => { if (n.getId() === msg.tabId) found = true; });
          if (found) {
            try {
              m.doAction(Actions.deleteTab(msg.tabId));
              saveLayoutRef.current(m);
            } catch (err) { console.warn('[LayoutManager] synced-tab-removed deleteTab failed:', err); }
          }
        }
        // Server-pushed layout update: apply without reload (triggered by POST /api/layouts/:projectId)
        if (msg.type === 'control:apply-layout' && msg.projectId === projectId && msg.layout
            && (!msg.userId || msg.userId === myUserIdRef.current)) {
          try {
            const serverV = typeof msg.layout._v === 'number' ? msg.layout._v : -1;
            // Only apply if server version is newer (or unversioned) — prevents processing our own echo
            if (serverV > currentLayoutVersionRef.current || serverV === -1) {
              // Self-echo detection: server broadcasts our own POST back to us. The race
              // condition where serverV arrives before the POST response means the version
              // check alone fails. Instead, compare layout content (sans _v) against what we
              // just sent. If identical, just bump version + skip setModel — no re-mount.
              let isSelfEcho = false;
              try {
                const { _v, ...incomingNoV } = msg.layout;
                if (lastSavedJsonRef.current && JSON.stringify(incomingNoV) === lastSavedJsonRef.current) {
                  isSelfEcho = true;
                }
              } catch { /* compare failed; proceed normally */ }
              if (isSelfEcho) {
                // Server is broadcasting our own POST back; bump version, don't re-apply
                if (serverV >= 0) currentLayoutVersionRef.current = serverV;
                return;
              }
              // User just made an explicit layout change locally (Layout button /
              // unstack / builder / reset) — a server broadcast must not clobber it.
              // Adopt the version, keep the local layout, and re-assert it upstream.
              if (Date.now() < userLayoutActionUntilRef.current) {
                if (serverV >= 0) currentLayoutVersionRef.current = serverV;
                logCuiTelemetry({ ts: Date.now(), kind: 'ws-apply-layout-local-wins', trigger: 'ws:apply-layout-broadcast', projectId, serverV });
                const localM = modelRef.current;
                if (localM) saveLayoutRef.current?.(localM);
                return;
              }
              if (serverV >= 0) currentLayoutVersionRef.current = serverV;
              // Suppression must outlast handleModelChange's 1500ms debounce (otherwise post-setModel
              // onChange wave fires saveLayout → 409 → re-mount loop, with WS disconnect storm).
              suppressUntilRef.current = Date.now() + 2500;
              if (saveTimer.current) clearTimeout(saveTimer.current);
              // Try in-place merge first — preserves child panels' WS connections (no re-mount).
              // Falls back to setModel only when tab-sets truly differ (panes added/removed/restructured).
              const merged = tryMergeServerLayout(modelRef.current, msg.layout as { layout?: unknown });
              logCuiTelemetry({ ts: Date.now(), kind: 'ws-apply-layout', trigger: 'ws:apply-layout-broadcast', projectId, merged, serverV });
              // Don't resurrect a panel the user just closed (tombstone strip).
              const safeLayout = merged ? msg.layout : stripTombstonedTabs(msg.layout, isComponentTombstoned, isSessionTombstoned);
              if (!merged) {
                setModel(Model.fromJson(eagerifyLayoutJson(safeLayout as IJsonModel)));
              }
              try {
                // Persist the LOCAL structure when merged (attrs-only patch in-place);
                // otherwise the tombstone-stripped server layout.
                const lm = modelRef.current;
                const toCache = merged && lm
                  ? { ...lm.toJson(), _v: currentLayoutVersionRef.current }
                  : safeLayout;
                localStorage.setItem(`cui-layout-${layoutId}`, JSON.stringify(toCache));
              } catch (e) { /* ignore */ }
              setTimeout(reportPanels, 200);
            }
          } catch (err) { console.warn('[LayoutManager] apply-layout failed:', err); }
        }
        // `projectId` ist optional und wirkt nur, wenn der Absender ihn setzt:
        // Bestandsaufrufer (Screenshot-Pfad) schicken ihn nicht und treffen wie
        // bisher jeden geladenen Workspace. Der Guide setzt ihn, weil eine Karte
        // ihre Panels genau EINEM Workspace zuordnet — ohne diese Einschraenkung
        // legt ein Klick in der Guide-Spalte dasselbe Panel in jedem geladenen
        // Workspace an.
        const giltHier = (zielProjectId?: string) => !zielProjectId || zielProjectId === projectId;
        if (msg.type === 'control:select-tab' && m && msg.target && giltHier(msg.projectId)) {
          let foundId = '';
          m.visitNodes((node) => {
            if (foundId) return;
            if (node.getType() === 'tab') {
              const tab = node as TabNode;
              if (tab.getId() === msg.target || tab.getComponent() === msg.target) {
                foundId = tab.getId();
              }
            }
          });
          if (foundId) {
            try {
              m.doAction(Actions.selectTab(foundId));
              ws.send(JSON.stringify({ type: 'tab-selected', nodeId: foundId, target: msg.target }));
            } catch (err) { console.warn('[LayoutManager] select-tab doAction failed:', err); }
          } else {
            ws.send(JSON.stringify({ type: 'tab-select-failed', target: msg.target, error: 'not found' }));
          }
        }
        if (msg.type === 'control:ensure-panel' && m && msg.component && giltHier(msg.projectId)) {
          let foundId = '';
          m.visitNodes((node) => {
            if (foundId) return;
            // Callers sometimes pass a nodeId where a component name is expected —
            // accept a direct nodeId match so it resolves instead of creating junk.
            if (node.getId() === msg.component) { foundId = node.getId(); return; }
            if (node.getType() === 'tab') {
              const tab = node as TabNode;
              if (tab.getComponent() === msg.component) foundId = tab.getId();
            }
          });
          // Only ever synthesise a panel for a real, renderable component type —
          // never for a stray nodeId or an embedded-panel id like "toolhub-browser".
          const SYNTHESISABLE = new Set([
            'activity-feed', 'architecture', 'background-ops', 'bridge-monitor', 'browser',
            'business-angel', 'business-docs', 'business-overview', 'sales', 'calendar', 'conversation-queue', 'cui', 'cui-lite',
            'error-monitor', 'feedback', 'gmail', 'images', 'infisical-monitor', 'infrastructure',
            'input-audit', 'knowledge', 'knowledge-fullscreen', 'linkedin', 'mail', 'maintenance',
            'mission', 'mission-chat', 'my-tasks', 'netzwerk', 'notes', 'partner-inbox', 'customer-comms', 'partner-server',
            'partner-browsers',
            'peer-awareness', 'platform-admin', 'preview', 'privat-angel', 'prompt-explorer',
            'qa-dashboard', 'repo-dashboard', 'report-builder', 'sandbox-business-angel',
            'sandbox-privat-angel', 'sandbox-rafael-angel', 'sub-sessions', 'system-health',
            'team-status', 'tool-hub', 'uploads', 'virtual-office', 'watchdog',
          ]);
          if (!foundId && SYNTHESISABLE.has(msg.component)) {
            let targetId = '';
            m.visitNodes((node) => { if (!targetId && node.getType() === 'tabset') targetId = node.getId(); });
            if (targetId) {
              const nameMap: Record<string, string> = { 'browser': 'Browser', 'images': 'Images', 'notes': 'Notes' };
              try {
                m.doAction(Actions.addNode(
                  { type: 'tab', name: nameMap[msg.component] || msg.component, component: msg.component, config: msg.config || {} },
                  targetId, DockLocation.CENTER, -1
                ));
              } catch (err) { console.warn('[LayoutManager] ensure-panel addNode failed:', err); }
              m.visitNodes((node) => {
                if (node.getType() === 'tab') {
                  const tab = node as TabNode;
                  if (tab.getComponent() === msg.component) foundId = tab.getId();
                }
              });
            }
          }
          if (foundId) {
            try {
              m.doAction(Actions.selectTab(foundId));
            } catch (err) { console.warn('[LayoutManager] ensure-panel selectTab failed:', err); }
            reportPanels();
            ws.send(JSON.stringify({ type: 'panel-ensured', nodeId: foundId, component: msg.component }));
          } else {
            ws.send(JSON.stringify({ type: 'panel-ensure-failed', component: msg.component, error: 'could not add' }));
          }
        }
        // Split tabsets that contain multiple CUI panels into separate panels
        if (msg.type === 'control:split-cui-panels' && m) {
          let tabsetCount = 0;
          m.visitNodes((node) => { if (node.getType() === 'tabset') tabsetCount++; });

          let changed = false;
          // Collect tabsets that have >1 CUI tab
          const tabsetsToSplit: Array<{ tabsetId: string; extraTabIds: string[] }> = [];
          m.visitNodes((node) => {
            if (node.getType() !== 'tabset') return;
            const ts = node as TabSetNode;
            const cuiTabs = (ts.getChildren() as TabNode[]).filter(
              t => t.getType() === 'tab' && (t.getComponent() === 'cui' || t.getComponent() === 'cui-lite')
            );
            if (cuiTabs.length > 1) {
              // Keep the first, split out the rest
              tabsetsToSplit.push({ tabsetId: ts.getId(), extraTabIds: cuiTabs.slice(1).map(t => t.getId()) });
            }
          });

          for (const { tabsetId, extraTabIds } of tabsetsToSplit) {
            for (const tabId of extraTabIds) {
              // Dock relative to the CUI tabset itself — flexlayout splits it into a sibling tabset
              const targetId = tabsetId;

              const dockLocation = DockLocation.RIGHT;
              const tab = m.getNodeById(tabId) as TabNode | undefined;
              if (!tab) continue;
              const tabJson = { type: 'tab' as const, name: tab.getName(), component: tab.getComponent() || 'cui', config: { ...tab.getConfig() } };
              try {
                m.doAction(Actions.moveNode(tabId, targetId, dockLocation, -1));
                tabsetCount++;
                changed = true;
              } catch {
                // moveNode not available — add new + delete old
                try {
                  m.doAction(Actions.addNode(tabJson, targetId, dockLocation, -1));
                  m.doAction(Actions.deleteTab(tabId));
                  tabsetCount++;
                  changed = true;
                } catch (err) { console.warn('[LayoutManager] split-cui-panels failed:', err); }
              }
            }
          }
          if (changed) { saveLayoutRef.current(m); reportPanels(); }
        }
        if (msg.type === 'control:activate-conversations' && m && msg.plan) {
          const myPlan = (msg.plan as Array<{ projectId: string; conversations: Array<{ sessionId: string; accountId: string }> }>)
            .find(p => p.projectId === projectId);
          if (!myPlan) return;

          const existingPanels: string[] = [];
          m.visitNodes((node) => {
            if (node.getType() === 'tab') {
              const tab = node as TabNode;
              // Only use cui/cui-lite panels for activation — never mission-chat (reserved panel)
              if (tab.getComponent() === 'cui' || tab.getComponent() === 'cui-lite') {
                existingPanels.push(tab.getId());
              }
            }
          });

          const assignments: Array<{ panelId: string; sessionId: string }> = [];
          const usedPanels = new Set<string>();
          const unmatched: Array<{ sessionId: string; accountId: string }> = [];

          for (const conv of myPlan.conversations) {
            const panel = existingPanels.find(id => !usedPanels.has(id));
            if (panel) {
              assignments.push({ panelId: panel, sessionId: conv.sessionId });
              usedPanels.add(panel);
            } else {
              unmatched.push(conv);
            }
          }

          let tabsetCount = 0;
          m.visitNodes((node) => { if (node.getType() === 'tabset') tabsetCount++; });

          // Only create new panels if auto-layout is explicitly triggered (not automatic)
          if (!window.__cuiAutoLayoutActive && unmatched.length > 0) {
            console.log(`[LM] activate-conversations: ${unmatched.length} unmatched convs skipped (auto-layout disabled)`);
          }

          for (const conv of window.__cuiAutoLayoutActive ? unmatched : []) {
            // Target an existing CUI tabset directly — flexlayout splits it into a sibling tabset
            let targetId = '';
            m.visitNodes((node) => {
              if (node.getType() === 'tab' && !targetId) {
                const tab = node as TabNode;
                if (tab.getComponent() === 'cui' || tab.getComponent() === 'cui-lite') {
                  const parent = tab.getParent();
                  if (parent && parent.getType() === 'tabset') targetId = parent.getId();
                }
              }
            });
            // Fallback: any tabset
            if (!targetId) {
              m.visitNodes((node) => { if (node.getType() === 'tabset' && !targetId) targetId = node.getId(); });
            }
            if (!targetId) continue;

            const dockLocation = tabsetCount < 6
              ? DockLocation.RIGHT
              : DockLocation.CENTER;

            try {
              m.doAction(Actions.addNode(
                { type: 'tab', name: 'Chat', component: 'cui', config: {} },
                targetId, dockLocation, -1
              ));
            } catch (err) { console.warn('[LayoutManager] activate-conversations addNode failed:', err); continue; }
            tabsetCount++;

            let newPanelId = '';
            m.visitNodes((node) => {
              if (node.getType() === 'tab') {
                const tab = node as TabNode;
                if ((tab.getComponent() === 'cui' || tab.getComponent() === 'cui-lite')
                    && !usedPanels.has(tab.getId()) && !assignments.some(a => a.panelId === tab.getId())) {
                  newPanelId = tab.getId();
                }
              }
            });
            if (newPanelId) {
              assignments.push({ panelId: newPanelId, sessionId: conv.sessionId });
              usedPanels.add(newPanelId);
            }
          }

          assignments.forEach((a, i) => {
            setTimeout(() => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'navigate-request', panelId: a.panelId, sessionId: a.sessionId, projectId }));
              }
            }, i * 300);
          });

          saveLayoutRef.current(m);
          reportPanels();
        }
        // Track per-session states for tab indicators
        if (msg.type === 'conv-attention' && msg.sessionId) {
          const prev = sessionStatesRef.current.get(msg.sessionId);
          const newState = msg.state || 'idle';
          const newReason = msg.reason;
          if (!prev || prev.state !== newState || prev.reason !== newReason) {
            sessionStatesRef.current.set(msg.sessionId, { state: newState, reason: newReason });
            setTabRenderTick(t => t + 1);
          }
        }
        if (msg.type === 'cui-state' && msg.sessionId) {
          const mapped = msg.state === 'processing' ? 'working' : msg.state === 'done' ? 'idle' : msg.state;
          const prev = sessionStatesRef.current.get(msg.sessionId);
          if (!prev || prev.state !== mapped) {
            sessionStatesRef.current.set(msg.sessionId, { state: mapped, reason: prev?.reason });
            setTabRenderTick(t => t + 1);
          }
        }
        // Bulk session state init (WS reconnect recovery)
        if (msg.type === "session-states-init" && msg.states) {
          const states = msg.states as Record<string, { state: string; reason?: string; sessionId?: string }>;
          let changed = false;
          for (const [key, val] of Object.entries(states)) {
            const sid = val.sessionId || key;
            const mapped = val.state === "working" ? "working" : val.state === "needs_attention" ? "needs_attention" : "idle";
            const prev = sessionStatesRef.current.get(sid);
            if (!prev || prev.state !== mapped || prev.reason !== val.reason) {
              sessionStatesRef.current.set(sid, { state: mapped, reason: val.reason });
              changed = true;
            }
          }
          if (changed) setTabRenderTick(t => t + 1);
        }
        // Pause state: suppress needs_attention indicator
        if (msg.type === 'conv-paused' && msg.sessionId) {
          if (msg.paused) {
            pausedSessionsRef.current.add(msg.sessionId);
          } else {
            pausedSessionsRef.current.delete(msg.sessionId);
          }
          setTabRenderTick(t => t + 1);
        }
      } catch (err) { console.warn('[LayoutManager] WS message handler error:', err); }
    };
    };

    connect();

    // Listen for SessionStore reconnection — immediately reconnect control WS
    const onServerReconnected = () => {
      if (disposed) return;
      console.log('[LayoutManager WS] Server reconnected via SessionStore, immediate reconnect');
      if (reconnectTimer) clearTimeout(reconnectTimer);
      backoff = 1000;
      const existing = controlWsRef.current;
      if (existing) { existing.onclose = null; existing.close(); controlWsRef.current = null; }
      connect();
    };
    window.addEventListener('cui-reconnected', onServerReconnected);

    return () => {
      disposed = true;
      window.removeEventListener('cui-reconnected', onServerReconnected);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      const ws = controlWsRef.current;
      controlWsRef.current = null;
      ws?.close();
    };
  }, [projectId]); // Only reconnect when project changes, not on every model update

  // Process pending activation plan (from prop, e.g. after project switch)
  useEffect(() => {
    if (!pendingActivation || !model) return;
    const myPlan = pendingActivation.find(p => p.projectId === projectId);
    if (!myPlan) return;

    // Wait for WS to connect before sending navigate commands
    const tryProcess = () => {
      const ws = controlWsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        // WS not ready yet - retry shortly
        setTimeout(tryProcess, 200);
        return;
      }

      // Same logic as the WS handler: inventory, match, split, navigate (generic, no account binding)
      // Only use cui/cui-lite panels — never mission-chat (reserved panel)
      const existingPanels: string[] = [];
      model.visitNodes((node) => {
        if (node.getType() === 'tab') {
          const tab = node as TabNode;
          if (tab.getComponent() === 'cui' || tab.getComponent() === 'cui-lite') {
            existingPanels.push(tab.getId());
          }
        }
      });

      const assignments: Array<{ panelId: string; sessionId: string }> = [];
      const usedPanels = new Set<string>();
      const unmatched: Array<{ sessionId: string; accountId: string }> = [];

      for (const conv of myPlan.conversations) {
        const panel = existingPanels.find(id => !usedPanels.has(id));
        if (panel) {
          assignments.push({ panelId: panel, sessionId: conv.sessionId });
          usedPanels.add(panel);
        } else {
          unmatched.push(conv);
        }
      }

      let tabsetCount = 0;
      model.visitNodes((node) => { if (node.getType() === 'tabset') tabsetCount++; });

      for (const conv of unmatched) {
        // Find a tabset and use its PARENT as target — this creates a new separate panel
        // (targeting the tabset itself would just add a tab into it)
        let targetId = '';
        let fallbackTabsetId = '';
        model.visitNodes((node) => {
          if (node.getType() === 'tabset' && !targetId) {
            const ts = node as TabSetNode;
            fallbackTabsetId = ts.getId();
            const parent = ts.getParent();
            if (parent) targetId = parent.getId();
          }
        });
        if (!targetId) {
          targetId = fallbackTabsetId;
          if (!targetId) continue;
        }

        const dockLocation = tabsetCount < 6
          ? DockLocation.RIGHT
          : DockLocation.CENTER;

        try {
          model.doAction(Actions.addNode(
            { type: 'tab', name: 'Chat', component: 'cui', config: {} },
            targetId, dockLocation, -1
          ));
        } catch (err) { console.warn('[LayoutManager] pendingActivation addNode failed:', err); continue; }
        tabsetCount++;

        let newPanelId = '';
        model.visitNodes((node) => {
          if (node.getType() === 'tab') {
            const tab = node as TabNode;
            if ((tab.getComponent() === 'cui' || tab.getComponent() === 'cui-lite')
                && !usedPanels.has(tab.getId()) && !assignments.some(a => a.panelId === tab.getId())) {
              newPanelId = tab.getId();
            }
          }
        });
        if (newPanelId) {
          assignments.push({ panelId: newPanelId, sessionId: conv.sessionId });
          usedPanels.add(newPanelId);
        }
      }

      // Record when we send navigate-requests so pruneEmptyCui can guard against
      // prematurely deleting panels whose routes haven't been set by onRouteChange yet.
      lastNavigateRequestSentAtRef.current = Date.now();
      assignments.forEach((a, i) => {
        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'navigate-request', panelId: a.panelId, sessionId: a.sessionId, projectId }));
          }
        }, i * 300);
      });

      saveLayout(model);
      onActivationProcessed?.(projectId);
    };

    tryProcess();
  }, [pendingActivation, model, projectId, onActivationProcessed, saveLayout]);

  // Auto-mount ongoing (non-finished) conversations for this project as tabs
  // Continuous auto-sync: periodically mount missing conversations, close finished ones
  const syncNowRef = useRef<((trigger: string, correlationId?: string) => void) | null>(null);
  // Tracks whether syncConversations has run at least once for this LayoutManager mount.
  // The FIRST sync after mount may claim empty panels (page-load recovery: re-mount sessions
  // into panels that lost their initialSessionId after layout cache eviction). Subsequent
  // periodic syncs only modify the model when the user explicitly clicks Layout
  // (__cuiAutoLayoutActive). This preserves the user-promise: "auto-sync rührt nichts an".
  const hasInitialSyncRunRef = useRef(false);
  useEffect(() => {
    if (!modelInitialized || !workDir) return;
    // Shadow client mirrors server layouts via apply-layout broadcasts only —
    // no auto-sync mutations (mount/prune/equalize). saveLayout is a no-op in
    // shadow mode anyway; skipping the whole loop also stops the local model
    // from drifting away from what the server (and the users) actually have.
    if (isShadowClient()) return;
    let disposed = false;

    const syncConversations = async (trigger: string, correlationId: string = newCorrelationId()) => {
      const m = modelRef.current;
      const ws = controlWsRef.current;
      if (!m || disposed || window.__cuiServerAlive === false) return;

      try {
        // Unstack: when user explicitly clicked Layout, move every cui/cui-lite tab that
        // shares a tabset with another cui-tab into its own sibling tabset (split RIGHT).
        // Goal: all open chats visible side-by-side, no hidden stacks.
        if (window.__cuiAutoLayoutActive) {
          const tabsetCuiTabs = new Map<string, string[]>();
          m.visitNodes((node) => {
            if (node.getType() !== 'tab') return;
            const tab = node as TabNode;
            const comp = tab.getComponent();
            if (comp !== 'cui' && comp !== 'cui-lite') return;
            const parent = tab.getParent();
            if (!parent || parent.getType() !== 'tabset') return;
            const arr = tabsetCuiTabs.get(parent.getId()) || [];
            arr.push(tab.getId());
            tabsetCuiTabs.set(parent.getId(), arr);
          });
          let unstacked = 0;
          for (const [tsid, tabs] of tabsetCuiTabs) {
            if (tabs.length <= 1) continue;
            for (let i = 1; i < tabs.length; i++) {
              try {
                m.doAction(Actions.moveNode(tabs[i], tsid, DockLocation.RIGHT, -1));
                unstacked++;
              } catch (err) { console.warn('[LM] unstack moveNode failed:', err); }
            }
          }
          if (unstacked > 0) {
            // Unstack is an explicit user layout action — it must win a concurrent
            // server-version race, otherwise the chats snap back into one tabset.
            userLayoutActionUntilRef.current = Date.now() + USER_LAYOUT_ACTION_MS;
            saveLayoutRef.current(m);
            console.log(`[LM] Unstacked ${unstacked} cui tabs into separate tabsets`);
          }
        }

        // Filter by projectId (precise — resolves multi-workspace users like David/Sahori).
        // Server accepts either projectId or workDir for backwards compat.
        const projFilter = projectId || workDir;
        const res = await fetch(conversationsUrl('/api/mission/conversations', { project: projFilter }),
          { signal: AbortSignal.timeout(10000) });
        if (!res.ok || disposed) return;
        const data = await res.json();
        const conversations: any[] = data.conversations || [];

        // Inventory mounted CUI tabs (and mission-chat tabs to avoid routing sessions there).
        // mountedSessions tracks ONLY cui/cui-lite tabs — these are the ones the cleanup
        // loop is allowed to delete. mission-chat panels are tracked separately so we
        // don't double-mount sessions there, but they are NEVER candidates for deletion.
        const mountedSessions = new Map<string, string>(); // sessionId -> cui/cui-lite nodeId (canonical = first)
        const allMountsBySid = new Map<string, string[]>(); // sessionId -> ALL nodeIds (for duplicate detection)
        const sessionsInMissionChat = new Set<string>();    // sessions currently shown in a mission-chat panel
        const emptyPanels: string[] = [];
        m.visitNodes((node) => {
          if (node.getType() === 'tab') {
            const tab = node as TabNode;
            const comp = tab.getComponent?.();
            if (comp === 'mission-chat') {
              const route = getNodeRoute(tab) || '';
              const sid = route.startsWith('/c/') ? route.slice(3) : '';
              if (sid) sessionsInMissionChat.add(sid);
              return;
            }
            if (comp !== 'cui' && comp !== 'cui-lite') return;
            const route = getNodeRoute(tab) || '';
            const cfgSid = tab.getConfig()?.initialSessionId || '';
            const sid = route.startsWith('/c/') ? route.slice(3) : cfgSid || '';
            if (sid) {
              if (!mountedSessions.has(sid)) mountedSessions.set(sid, tab.getId());
              const arr = allMountsBySid.get(sid) || [];
              arr.push(tab.getId());
              allMountsBySid.set(sid, arr);
              // Ensure config has initialSessionId (triggers useEffect in CuiLitePanel to un-stuck Queue)
              // Gate: only patch when initial sync OR user explicitly clicked Layout — never overwrite
              // a recently user-reserved panel (Rule 3: auto-sync rührt nichts an).
              if (!cfgSid && (hasInitialSyncRunRef.current === false || window.__cuiAutoLayoutActive)) {
                const existingCfg = tab.getConfig() ?? {};
                const isUserReserved = existingCfg._userReserved && Date.now() - existingCfg._userReserved < 30 * 60_000; // 30min compose window (was 60s)
                if (!isUserReserved) {
                  try {
                    m.doAction(Actions.updateNodeAttributes(tab.getId(), {
                      config: { ...existingCfg, initialSessionId: sid }
                    }));
                  } catch {} // silent-ok: FlexLayout node attribute update is best-effort for session routing
                }
              }
            } else {
              emptyPanels.push(tab.getId());
            }
          }
        });
        // Rule 1 (isOpenChat) decides EXISTENCE — a session gets a tab until Finish.
        // Rule 2: Sub-Sessions are NEVER shown in normal workspaces — see utils/subSession.ts.
        //
        // Rule 3 (liveness==='stuck') is deliberately NOT applied here any more. It used to
        // be, to stop week-old dead research delegations from inflating the Layout badge past
        // what was mountable ("Layout (6)" while only 4 were real, 2026-07-30). But filtering
        // it out HERE fixes the badge by making the chat unreachable, which is the failure
        // from 2026-08-12 — an open chat with no tab — and is what Rule 3's own documentation
        // forbids: it decides urgency, never existence. Badge and mount now read the same
        // rule, so they cannot disagree in either direction.
        //
        // The 2026-07-30 symptom has a different cure: those delegations are open chats and
        // belong in the list until someone finishes them. That is what the Finish button and
        // the open-only default of GET /api/mission/conversations are for.
        const active = conversations.filter((c: any) => isOpenChat(c) && !isSubSession(c));
        const activeSessionIds = new Set(active.map((c: any) => c.sessionId));

        // "Before" snapshot — what the Layout badge claims (active.length, rendered as
        // "Layout (N)" in ProjectTabs) vs what's actually mounted in the FlexLayout tree
        // right now, BEFORE this sync mutates anything. Comparing this to the "after"
        // snapshot at the end of the function (same correlationId) is how you catch
        // "badge said 2, but 3 panels appeared" — the exact class of bug reported.
        const totalCuiMountsBefore = [...allMountsBySid.values()].reduce((n, ids) => n + ids.length, 0);
        logCuiTelemetry({
          ts: Date.now(), kind: 'lm-sync-snapshot-before', trigger, correlationId, projectId,
          badgeCount: active.length, distinctSessionsMounted: mountedSessions.size,
          totalCuiTabMounts: totalCuiMountsBefore, emptyCuiPanels: emptyPanels.length,
          duplicateMounts: totalCuiMountsBefore - mountedSessions.size,
        });

        // "After" snapshot — re-walks the live model (fresh, post-mutation) and logs the
        // same shape as the "before" snapshot above so the two can be diffed by
        // correlationId. Only emitted for user-initiated triggers (Layout button, WS
        // reconnect, project-switch, ...) — the silent 30s auto-tick fires this function
        // ~2x/min and is a no-op almost every time, so logging its after-state too would
        // flood the file without adding diagnostic value.
        const logAfterSnapshot = () => {
          if (!trigger.startsWith('user:') && trigger !== 'ws-reconnect' && trigger !== 'project-switch') return;
          let totalCuiTabs = 0;
          let emptyCuiTabs = 0;
          const distinctSessions = new Set<string>();
          m.visitNodes((node) => {
            if (node.getType() !== 'tab') return;
            const tab = node as TabNode;
            const comp = tab.getComponent?.();
            if (comp !== 'cui' && comp !== 'cui-lite') return;
            totalCuiTabs++;
            const route = getNodeRoute(tab) || '';
            const cfgSid = tab.getConfig()?.initialSessionId || '';
            const sid = route.startsWith('/c/') ? route.slice(3) : cfgSid || '';
            if (sid) distinctSessions.add(sid); else emptyCuiTabs++;
          });
          logCuiTelemetry({
            ts: Date.now(), kind: 'lm-sync-snapshot-after', trigger, correlationId, projectId,
            badgeCount: active.length, distinctSessionsMounted: distinctSessions.size,
            totalCuiTabMounts: totalCuiTabs, emptyCuiPanels: emptyCuiTabs,
            duplicateMounts: totalCuiTabs - distinctSessions.size - emptyCuiTabs,
          });
        };

        // Duplicate-cleanup: same sessionId mounted on multiple cui tabs.
        // Happens when server-persisted layout drifts (apply-layout merge edge cases,
        // user-driven tab splits, race conditions in addNode). Always remove duplicates,
        // not gated on __cuiAutoLayoutActive — duplicates make the chat appear twice
        // and confuse session-claim eviction.
        let duplicatesRemoved = 0;
        for (const [sid, ids] of allMountsBySid) {
          if (ids.length <= 1) continue;
          // Keep the FIRST (canonical = the one mountedSessions points to).
          // Delete the rest.
          for (let i = 1; i < ids.length; i++) {
            try {
              m.doAction(Actions.deleteTab(ids[i]));
              duplicatesRemoved++;
            } catch (err) { console.warn('[LM] duplicate deleteTab failed:', err); }
          }
          logCuiTelemetry({
            ts: Date.now(), kind: 'lm-duplicate-detected', trigger, correlationId, projectId,
            sessionId: sid.slice(0, 8), totalMounts: ids.length, kept: ids[0], removed: ids.slice(1)
          });
        }
        if (duplicatesRemoved > 0) {
          saveLayoutRef.current(m);
          console.log(`[LM] Removed ${duplicatesRemoved} duplicate cui tabs`);
        }

        // Singleton-component cleanup: certain panel types should exist at most ONCE per
        // workspace (Platform Admin, Tool Hub, Watchdog, etc.). Duplicates accumulate via
        // racy `control:ensure-panel` broadcasts and `synced-tab-added` echoes — each
        // adds a new tab with a random UUID before the previous addNode has settled.
        // Multi-instance panels (cui, browser, preview, notes, images) are NEVER deduped.
        const SINGLETON_COMPONENTS = new Set([
          'platform-admin', 'tool-hub', 'system-health', 'watchdog',
          'repo-dashboard', 'infisical-monitor', 'bridge-monitor', 'mission',
          'mission-chat', 'conversation-queue', 'background-ops', 'maintenance',
          'input-audit', 'qa-dashboard', 'peer-awareness', 'architecture',
          'report-builder', 'prompt-explorer', 'sub-sessions', 'business-docs',
          'sales', 'business-overview',
          'my-tasks', 'activity-feed', 'partner-inbox', 'customer-comms', 'netzwerk', 'feedback', 'team-status',
          'uploads', 'linkedin', 'gmail', 'partner-server', 'partner-browsers', 'knowledge', 'virtual-office',
        ]);
        const singletonsByComp = new Map<string, string[]>(); // component -> nodeIds (insertion order)
        m.visitNodes((node) => {
          if (node.getType() !== 'tab') return;
          const tab = node as TabNode;
          const comp = tab.getComponent?.() || '';
          if (!SINGLETON_COMPONENTS.has(comp)) return;
          const arr = singletonsByComp.get(comp) || [];
          arr.push(tab.getId());
          singletonsByComp.set(comp, arr);
        });
        let singletonsRemoved = 0;
        for (const [comp, ids] of singletonsByComp) {
          if (ids.length <= 1) continue;
          for (let i = 1; i < ids.length; i++) {
            try {
              m.doAction(Actions.deleteTab(ids[i]));
              singletonsRemoved++;
            } catch (err) { console.warn('[LM] singleton deleteTab failed:', err); }
          }
          logCuiTelemetry({
            ts: Date.now(), kind: 'lm-singleton-dupe', trigger, correlationId, projectId,
            component: comp, totalMounts: ids.length, kept: ids[0], removed: ids.slice(1),
          });
        }
        if (singletonsRemoved > 0) {
          saveLayoutRef.current(m);
          console.log(`[LM] Removed ${singletonsRemoved} duplicate singleton tab(s)`);
        }

        // Cleanup: remove tabs whose session is no longer active (finished or too old)
        // ONLY when user explicitly clicked Layout button (prevents sessions from disappearing)
        let removed = 0;
        if (window.__cuiAutoLayoutActive) {
          for (const [sid, nodeId] of mountedSessions) {
            // Keep if session is in active list
            if (activeSessionIds.has(sid)) continue;
            // Keep panels reserved for new session creation (placeholder route)
            if (sid === '_starting') continue;
            // Remove stale tab
            try {
              m.doAction(Actions.deleteTab(nodeId));
              removed++;
            } catch (err) { console.warn('[LM] cleanup deleteTab failed:', err); }
          }
          if (removed > 0) {
            saveLayoutRef.current(m);
            logCuiTelemetry({ ts: Date.now(), kind: 'lm-cleanup', trigger, correlationId, projectId, removed, autoLayoutActive: !!window.__cuiAutoLayoutActive });
            console.log(`[LM] Cleanup: removed ${removed} stale tabs`);
          }
        }

        const missing = active.filter((c: any) => !mountedSessions.has(c.sessionId));

        // Report TOTAL open sessions count to parent (Layout button shows all open chats,
        // not just missing — user wants "see how many are open, click to arrange them all").
        onMissingSessionsRef.current?.(active.length);

        // Prune phantom empty chat panels. A cui tab with no session renders the
        // conversation picker ("Aktiv/Abgeschlossen"). The stale-session cleanup
        // above only touches mountedSessions (which require a sessionId), so an
        // empty cui tab is never removed — it lingers and equalizeCuiTabsets()
        // then counts it as an equal column. Symptom: badge says "2" but the row
        // shows 2 real chats + N empty picker panels (verified in partner-server
        // layout: 4 cui tabsets, 2 with sessionId '', telemetry equalized:4).
        // Only on an explicit Layout click, never in the 30s auto-sync. Skips
        // _userReserved panels (user is about to start a new chat) and keeps a
        // landing picker when nothing is active.
        const pruneEmptyCui = (): number => {
          // Keep landing picker when nothing is active.
          if (active.length === 0) return 0;
          const isExplicitClick = !!window.__cuiAutoLayoutActive;
          const isInitialSync = hasInitialSyncRunRef.current === false;
          // Periodic 30s sync stays read-only — only Layout-click + initial-mount
          // are allowed to delete an empty cui-tab.
          if (!isExplicitClick && !isInitialSync) return 0;
          // Race guard: pendingActivation sends navigate-requests, but onRouteChange
          // only fires after the CuiLitePanel round-trip (~600-900ms: WS transit +
          // pollNow 300ms delay + API fetch). The isActive 300ms sync fires BEFORE
          // that round-trip completes, so panels routed-but-not-yet-reflected are
          // seen as "empty" and deleted. Block prune for 1500ms after the last
          // navigate-request — explicit Layout-click overrides this guard.
          if (isInitialSync && !isExplicitClick &&
              Date.now() - lastNavigateRequestSentAtRef.current < 1500) {
            return 0;
          }
          const empties: string[] = [];
          m.visitNodes((node) => {
            if (node.getType() !== 'tab') return;
            const tab = node as TabNode;
            const c = tab.getComponent?.();
            if (c !== 'cui' && c !== 'cui-lite') return;
            const cfg = tab.getConfig() || {};
            const route = getNodeRoute(tab) || '';
            const sid = route.startsWith('/c/') ? route.slice(3) : (cfg.initialSessionId || '');
            if (sid) return;
            // _userReserved = picker the user just opened to start a new chat.
            // Initial sync and explicit clicks must both respect it.
            // 30min (was 60s): the reserve is stamped ONCE on tab creation and never
            // refreshed, so a 60s window pruned/reused the panel WHILE the user was
            // still composing a longer message (Sahori 2026-07: "if I write more than
            // 'test' it closes and opens an old chat"). A generous window covers human
            // compose time; a genuinely abandoned empty picker still recovers later.
            if (cfg._userReserved && Date.now() - cfg._userReserved < 30 * 60_000) return;
            empties.push(tab.getId());
          });
          // active.length === 0 already returned above (genuine "nothing
          // active, keep a landing picker" case). Here active.length >= 1, so
          // every session-less cui panel is noise — including the case where
          // the only conversation lives in a mission-chat panel (0 cui tabs
          // with a session). The old "never delete the last cui tabset" guard
          // misfired exactly there and kept the empty picker the user is
          // complaining about. Delete them all.
          const toDelete = empties;
          let pruned = 0;
          for (const id of toDelete) {
            try { m.doAction(Actions.deleteTab(id)); pruned++; }
            catch (err) { console.warn('[LM] prune empty cui failed:', err); }
          }
          if (pruned > 0) {
            logCuiTelemetry({ ts: Date.now(), kind: 'lm-prune-empty', trigger, correlationId, projectId, pruned });
            console.log(`[LM] Pruned ${pruned} empty chat panel(s)`);
          }
          return pruned;
        };

        // Prune phantom empty cui-tabs BEFORE mounting. A leftover empty cui-tab
        // (no initialSessionId, no route) renders a "ghost chat" by auto-loading
        // a SID from localStorage (CuiLitePanel.tsx:543) — the user sees the same
        // chat twice. pruneEmptyCui() itself gates on initial-sync vs explicit
        // click; respects _userReserved. Must run regardless of missing.length,
        // otherwise the reuse-path at the mount loop fills the sidebar-tab with
        // a chat and you end up with cui next to Tool Hub.
        const prunedEmpty = pruneEmptyCui();
        if (prunedEmpty > 0) {
          // Deleted nodeIds in emptyPanels would resolve to undefined nodes
          // in the mount loop's reuse path — skip reuse entirely, mount-loop
          // will create fresh tabsets via split.
          emptyPanels.length = 0;
          saveLayoutRef.current(m);
        }
        // Equalize columns when no new mounts pending (mount loop balances itself).
        if (window.__cuiAutoLayoutActive && missing.length === 0) {
          const eq = equalizeCuiTabsets(m);
          if (prunedEmpty > 0 || eq > 0) {
            saveLayoutRef.current(m);
            logCuiTelemetry({ ts: Date.now(), kind: 'lm-equalize', trigger, correlationId, projectId, equalized: eq, pruned: prunedEmpty });
            console.log(`[LM] Equalized ${eq} chat tabset(s), pruned ${prunedEmpty} empty`);
          }
        }

        if (missing.length === 0 && removed === 0 && duplicatesRemoved === 0 && prunedEmpty === 0) { logAfterSnapshot(); return; }
        if (missing.length === 0) {
          if (duplicatesRemoved > 0) {
            logCuiTelemetry({ ts: Date.now(), kind: 'lm-sync-done', trigger, correlationId, projectId, mounted: 0, removed, duplicatesRemoved, autoLayoutActive: !!window.__cuiAutoLayoutActive });
          }
          logAfterSnapshot();
          return;
        }

        let mounted = 0;
        const newlyMounted: Array<{ panelId: string; sessionId: string }> = [];

        for (const conv of missing) {
          // Race-protection: re-verify session isn't already mounted on a tab.
          // `mountedSessions` is built from a stale visitNodes snapshot — between
          // that scan and now, FlexLayout may have hydrated a tab's config (so
          // initialSessionId is now visible), or apply-layout may have arrived.
          // Skip if any cui/cui-lite tab currently reports this session.
          let alreadyMountedNow = false;
          m.visitNodes((node) => {
            if (alreadyMountedNow) return;
            if (node.getType() !== 'tab') return;
            const tab = node as TabNode;
            const tcomp = tab.getComponent?.();
            if (tcomp !== 'cui' && tcomp !== 'cui-lite') return;
            const tcfg = tab.getConfig() || {};
            if (tcfg.initialSessionId === conv.sessionId) { alreadyMountedNow = true; return; }
            const troute = getNodeRoute(tab) || '';
            if (troute === `/c/${conv.sessionId}`) alreadyMountedNow = true;
          });
          if (alreadyMountedNow) {
            logCuiTelemetry({ ts: Date.now(), kind: 'lm-skip-already-mounted-race', trigger, correlationId, projectId, sessionId: conv.sessionId?.slice(0, 8) });
            continue;
          }

          // Priority 1: Reuse an empty/stale CUI panel — just update its config
          // Gated: only on initial sync (page-load recovery for layouts whose initialSessionId
          // was lost) OR when user explicitly clicked Layout. Periodic auto-sync MUST NOT
          // claim user-touched panels (Rule 3: "auto-sync rührt nichts an").
          if (emptyPanels.length > 0 && (hasInitialSyncRunRef.current === false || window.__cuiAutoLayoutActive)) {
            const reuseNodeId = emptyPanels.shift()!;
            const existingCfg = (m.getNodeById(reuseNodeId) as TabNode)?.getConfig?.() ?? {};
            // Skip panels just reserved by the user — but ONLY for the
            // automatic 30s sync. An explicit Layout click is a direct command
            // and must be allowed to fill a reserved-but-empty panel with the
            // missing session (otherwise it spawns an extra split panel and the
            // reserved picker lingers — the reported toggle/"manche auf, manche
            // zu" symptom).
            if (!window.__cuiAutoLayoutActive && existingCfg._userReserved && Date.now() - existingCfg._userReserved < 30 * 60_000) { // 30min compose window (was 60s)
              emptyPanels.unshift(reuseNodeId); // put back, don't claim
            } else if (existingCfg.initialSessionId && existingCfg.initialSessionId !== conv.sessionId) {
              // Race: panel looked empty during visitNodes but now has a (different) session.
              // Don't overwrite — that would silently steal a tab from another conversation.
              logCuiTelemetry({ ts: Date.now(), kind: 'lm-skip-reuse-race', trigger, correlationId, projectId, panelId: reuseNodeId, hadSession: existingCfg.initialSessionId?.slice(0, 8), wantedSession: conv.sessionId?.slice(0, 8) });
            } else {
              try {
                m.doAction(Actions.updateNodeAttributes(reuseNodeId, {
                  config: { ...existingCfg, initialSessionId: conv.sessionId, accountId: conv.accountId }
                }));
                logCuiTelemetry({ ts: Date.now(), kind: 'lm-reuse-empty-panel', trigger, correlationId, projectId, panelId: reuseNodeId, sessionId: conv.sessionId?.slice(0, 8) });
                newlyMounted.push({ panelId: reuseNodeId, sessionId: conv.sessionId });
                mounted++;
                continue;
              } catch {} // silent-ok: panel config reuse failure is non-critical; session gets a new tab
            }
          }

          // Priority 2: Add as separate split panel — only when explicitly triggered
          if (!window.__cuiAutoLayoutActive) {
            // Silent: this branch fires every 30s for every unmounted session — would flood the console
            continue;
          }
          // Find a CUI tabset to split from (prefer one with existing CUI panels)
          let targetTabsetId = '';
          m.visitNodes((node) => {
            if (node.getType() === 'tab' && !targetTabsetId) {
              const tab = node as TabNode;
              if (tab.getComponent() === 'cui' || tab.getComponent() === 'cui-lite') {
                const parent = tab.getParent();
                if (parent && parent.getType() === 'tabset') targetTabsetId = parent.getId();
              }
            }
          });
          // Fallback: any tabset
          if (!targetTabsetId) {
            m.visitNodes((node) => {
              if (!targetTabsetId && node.getType() === 'tabset') targetTabsetId = node.getId();
            });
          }
          if (!targetTabsetId) continue;

          // Count current tabsets to determine split direction
          let tabsetCount = 0;
          m.visitNodes((node) => { if (node.getType() === 'tabset') tabsetCount++; });

          // Split as separate panel: always horizontal (side by side), CENTER as fallback
          const dockLocation = tabsetCount < 6
            ? DockLocation.RIGHT
            : DockLocation.CENTER;

          try {
            logCuiTelemetry({ ts: Date.now(), kind: 'lm-addnode-split', trigger, correlationId, projectId, sessionId: conv.sessionId?.slice(0, 8), targetTabsetId, tabsetCount });
            m.doAction(Actions.addNode(
              { type: 'tab', name: 'Chat', component: 'cui',
                config: { initialSessionId: conv.sessionId, accountId: conv.accountId } },
              targetTabsetId, dockLocation, -1
            ));
            // addNode generates a new nodeId — find it by scanning for the session
            m.visitNodes((node) => {
              if (node.getType() === 'tab') {
                const tab = node as TabNode;
                if (tab.getConfig()?.initialSessionId === conv.sessionId) {
                  newlyMounted.push({ panelId: tab.getId(), sessionId: conv.sessionId });
                }
              }
            });
            mounted++;
          } catch (err) { console.warn('[LM] auto-sync addNode failed:', err); }
        }

        // Send navigate-request ONLY for newly mounted panels (not all panels)
        if (newlyMounted.length > 0 && ws?.readyState === WebSocket.OPEN) {
          for (const ps of newlyMounted) {
            ws.send(JSON.stringify({ type: 'navigate-request', panelId: ps.panelId, sessionId: ps.sessionId, projectId }));
          }
        }

        // Reuse has now claimed every empty panel it needed for a missing
        // session; any cui tab still without a session is a phantom picker —
        // prune before equalizing so it doesn't get a column. Newly split-in
        // panels get an arbitrary FlexLayout weight — even them out so the
        // freshly arranged chats appear at equal width.
        const prunedAfter = pruneEmptyCui();
        const equalizedAfter = window.__cuiAutoLayoutActive ? equalizeCuiTabsets(m) : 0;

        if (mounted > 0 || equalizedAfter > 0 || prunedAfter > 0) {
          saveLayoutRef.current(m);
          logCuiTelemetry({ ts: Date.now(), kind: 'lm-sync-done', trigger, correlationId, projectId, mounted, removed, equalized: equalizedAfter, pruned: prunedAfter, autoLayoutActive: !!window.__cuiAutoLayoutActive });
          console.log(`[LM] Auto-sync: mounted ${mounted} conversations, equalized ${equalizedAfter} tabset(s), pruned ${prunedAfter} empty`);
        }
        logAfterSnapshot();
      } catch (err) { console.warn('[LM] auto-sync error:', err); }
      finally {
        // Mark initial sync done — after this, Priority-1 reuse and config-patch require
        // an explicit user click (Layout button) to mutate panels.
        hasInitialSyncRunRef.current = true;
      }
    };

    syncNowRef.current = syncConversations;

    // Initial sync after 2s (let layout settle)
    const initialTimer = setTimeout(() => { if (!disposed) syncConversations('initial-sync'); }, 2000);
    // Periodic sync every 30s
    const interval = setInterval(() => { if (!disposed) syncConversations('auto-30s'); }, 30000);

    return () => {
      disposed = true;
      syncNowRef.current = null;
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [modelInitialized, workDir, projectId]);

  // Tool Hub pin event: toggle sync for a tool by component name
  // Only the active workspace reacts (projectId filter in detail)
  useEffect(() => {
    if (!modelInitialized) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.component) return;
      // Only active workspace handles — prevents multiple mounted LayoutManagers from reacting
      if (detail.projectId && detail.projectId !== projectId) return;
      toggleSyncTool(detail.component, detail.name || detail.component);
    };
    window.addEventListener('cui-toggle-sync-tool', handler);
    return () => window.removeEventListener('cui-toggle-sync-tool', handler);
  }, [modelInitialized, projectId, toggleSyncTool]);

  // 'cui-open-panel': ein Panel nach Komponente oeffnen (PartnerOpenBanner, Sahori
  // 24.09.2026). Nur der aktive Arbeitsbereich reagiert. Ist das Panel schon im
  // Layout, wird es ausgewaehlt statt ein zweites Mal angelegt.
  useEffect(() => {
    if (!modelInitialized) return;
    const handler = (e: Event) => {
      const component = (e as CustomEvent).detail?.component;
      if (component !== 'partner-inbox' && component !== 'feedback' && component !== 'calendar' && component !== 'group-chat') return;
      if (!isActiveRef.current) return;
      const m = modelRef.current;
      if (!m) return;
      let vorhanden: string | null = null;
      let ersterTabset: string | null = null;
      m.visitNodes((n) => {
        if (!vorhanden && n.getType() === 'tab' && (n as TabNode).getComponent() === component) vorhanden = n.getId();
        if (!ersterTabset && n.getType() === 'tabset') ersterTabset = n.getId();
      });
      if (vorhanden) { m.doAction(Actions.selectTab(vorhanden)); return; }
      const ziel = m.getActiveTabset()?.getId() ?? ersterTabset;
      if (!ziel) { console.warn('[LayoutManager] cui-open-panel: kein Tabset fuer', component); return; }
      addTab(component, {}, ziel);
    };
    window.addEventListener('cui-open-panel', handler);
    return () => window.removeEventListener('cui-open-panel', handler);
  }, [modelInitialized, addTab]);

  // Manual sync trigger: 'cui-auto-layout' event (dispatched by Layout button in toolbar).
  // Mounts all open chats into the current layout without resetting it — the
  // __cuiAutoLayoutActive flag tells syncConversations to add unmatched chats as
  // new tabs (the default "automatic" sync only touches chats already in the layout).
  // Factory reset lives in the Cache button (wipes client + server layout).
  useEffect(() => {
    if (!modelInitialized) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.projectId && detail.projectId !== projectId) return;
      const correlationId = newCorrelationId();
      logCuiTelemetry({ ts: Date.now(), kind: 'lm-layout-button-clicked', trigger: 'user:layout-button', correlationId, projectId });
      window.__cuiAutoLayoutActive = true;
      // Explicit user layout action — its result must win a concurrent sync race.
      userLayoutActionUntilRef.current = Date.now() + USER_LAYOUT_ACTION_MS;
      syncNowRef.current?.('user:layout-button', correlationId);
      setTimeout(() => { window.__cuiAutoLayoutActive = false; }, 3000);
    };
    window.addEventListener('cui-auto-layout', handler);
    return () => window.removeEventListener('cui-auto-layout', handler);
  }, [modelInitialized, projectId]);

  // Trigger immediate sync + notify server when this project tab becomes active
  useEffect(() => {
    if (isActive) {
      // Force FlexLayout to recalculate dimensions after display:none → display:flex transition
      // Without this, FlexLayout may render with stale 0x0 dimensions from when the tab was hidden
      const redrawTimer = setTimeout(() => {
        const internal = (layoutRef.current as unknown as PatchableLayoutRef)?.selfRef?.current;
        if (internal?.redrawInternal) {
          internal.redrawInternal('project-switch');
        }
      }, 50); // 50ms: enough for CSS display:flex to take effect, before user sees black screen

      // Notify server of active project (sets activeProjectId for auto-layout)
      fetch(`/api/control/project/switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
      }).catch(() => { /* non-critical */ });
      // Also report panels with activeProjectId
      const ws = controlWsRef.current;
      if (ws?.readyState === WebSocket.OPEN) {
        const m = modelRef.current;
        if (m) {
          const panels: Array<{ id: string; component: string; config: Record<string, unknown>; name: string }> = [];
          sammleKacheln(m, panels);
          sammleKacheln(buehnenModelRef.current, panels);
          sammleKacheln(angeheftetModelRef.current, panels);
          ws.send(JSON.stringify({ type: 'state-report', panels, projectId, activeProjectId: projectId }));
        }
      }
      // Small delay to let CSS display:flex take effect before syncing conversations
      if (syncNowRef.current) {
        const t = setTimeout(() => syncNowRef.current?.('project-switch'), 300);
        return () => { clearTimeout(t); clearTimeout(redrawTimer); };
      }
      return () => clearTimeout(redrawTimer);
    }
  }, [isActive, projectId]);

  // Report attention state to parent (any CUI panel working, needs_attention, or idle with session)
  // Uses both panel-reported _attention AND WS-tracked sessionStatesRef for reliability
  useEffect(() => {
    if (!model || !onAttentionChange) return;
    let hasAttention = false;
    let highestState: 'working' | 'needs_attention' | 'idle' | undefined;
    let hasAnySession = false;
    model.visitNodes((node) => {
      if (node.getType() === 'tab') {
        const tab = node as TabNode;
        const comp = tab.getComponent();
        if (comp === 'cui' || comp === 'cui-lite') {
          const route = getNodeRoute(tab);
          const hasRoute = !!route;
          if (hasRoute) hasAnySession = true;

          // Primary: panel-reported attention (live, ephemeral)
          let effectiveState = getNodeAttention(tab);

          // Fallback: WS-tracked session state (more reliable for non-visible panels)
          const sessionId = route?.startsWith('/c/') ? route.slice(3) :
            (tab.getConfig()?.initialSessionId || tab.getConfig()?.sessionId || null);
          if (!effectiveState || effectiveState === 'idle') {
            if (sessionId) {
              const wsState = sessionStatesRef.current.get(sessionId);
              if (wsState && wsState.state !== 'idle') {
                effectiveState = wsState.state;
              }
            }
          }

          // Paused sessions: suppress needs_attention — don't bubble up to project indicator
          if (sessionId && pausedSessionsRef.current.has(sessionId)) {
            effectiveState = 'idle';
          }

          if (effectiveState === 'needs_attention') {
            hasAttention = true;
            highestState = 'needs_attention'; // highest priority
          } else if (effectiveState === 'working' && highestState !== 'needs_attention') {
            hasAttention = true;
            highestState = 'working';
          } else if (hasRoute && highestState !== 'needs_attention' && highestState !== 'working') {
            highestState = 'idle';
          }
        }
      }
    });
    // Report idle state if there are sessions but none are working/needs_attention
    if (!hasAttention && hasAnySession && highestState === 'idle') {
      onAttentionChange(false, 'idle');
    } else {
      onAttentionChange(hasAttention, highestState);
    }
  }, [model, cuiStates, onAttentionChange, attentionVersion, tabRenderTick]);

  // Patch flexlayout's redrawInternal to prevent continuous render loop
  useEffect(() => {
    if (layoutRef.current) patchLayoutRedraw(layoutRef.current as unknown as PatchableLayoutRef);
  }, [model]);

  // Memoize Layout element: Layout is a class component without shouldComponentUpdate.
  // Without this, every LayoutManager re-render (e.g. cuiStates change) causes
  // Layout.render() → this.revision++ → ALL visible tab content re-renders.
  // Must be before any early returns to satisfy React's rules of hooks.
  const layoutElement = useMemo(() => {
    if (!model) return null;
    return (
      <Layout
        ref={layoutRef}
        model={model}
        factory={factory}
        onModelChange={handleModelChange}
        onAction={handleAction}
        onRenderTabSet={stableOnRenderTabSet}
        onRenderTab={stableOnRenderTab}
      />
    );
  }, [model, factory, handleModelChange, handleAction, stableOnRenderTabSet, stableOnRenderTab]);

  // Stage arrangements are independent of the workspace layout. The stage component
  // owns view modes and persistence; report its current model for captures/control.
  const [buehnenModel, setBuehnenModel] = useState<Model | null>(null);
  const handleBuehnenModel = useCallback((m: Model | null) => {
    buehnenModelRef.current = m;
    setBuehnenModel(m);
    meldeKachelnRef.current?.();
  }, []);
  const handleAngeheftetModel = useCallback((m: Model | null) => {
    angeheftetModelRef.current = m;
    meldeKachelnRef.current?.();
  }, []);
  /*
   * BUEHNEN ALS LISTE MIT SCHLUESSEL (Teil 0 Anheften, 06.10.2026). Die gezeigte und die
   * angeheftete Buehne stehen als Geschwister im SELBEN Elternknoten, Schluessel = Buehnen-
   * Kennung. Darum montiert Anheften/Loesen/Zurueckwechseln nichts neu: React behaelt den
   * Knoten, nur Stil und Rueckmeldungen wechseln. Die angeheftete steht immer VORN — ein
   * <webview>, das im DOM verschoben wird, laedt in Electron neu.
   * Versteckt heisst visibility:hidden, NIE display:none (das haelt die Webviews an, siehe
   * die Arbeitsbereichs-Wurzeln in App.tsx).
   */
  const { eintraege: buehnenEintraege, kollision: buehnenKollision } = buehnenStapel(
    buehne ? { doc: buehne, bezug: buehnenBezug } : null, angeheftet,
  );
  const buehnenElement = buehnenEintraege.length ? buehnenEintraege.map(e => {
    const gesperrt = e.sichtbar && buehnenKollision.length > 0;
    return <div key={e.id}
      data-ai-id={e.sichtbar ? 'buehne' : 'buehne-angeheftet'}
      data-buehne-id={e.id}
      // Welche Buehne die sichtbare Schicht ZEIGT (buehnenStapel, `zeigt`); Oeffnungsbeleg vergleicht hiergegen.
      data-buehne-zeigt={e.zeigt}
      aria-hidden={e.sichtbar ? undefined : true}
      style={e.sichtbar
        ? { position: 'absolute', inset: 0, zIndex: 11, isolation: 'isolate', background: 'var(--tn-bg-dark)' }
        : { position: 'absolute', inset: 0, zIndex: -1, isolation: 'isolate', visibility: 'hidden', opacity: 0, pointerEvents: 'none' }}
    >
      {gesperrt ? <div data-ai-id="buehne-kollision" role="alert" style={{ padding: 16, color: 'var(--tn-red)', fontSize: 13, lineHeight: 1.5 }}>
        Diese Bühne hat {buehnenKollision.length === 1 ? 'ein Fenster' : `${buehnenKollision.length} Fenster`} mit derselben Kennung wie die angeheftete Bühne „{angeheftet?.id}“ ({buehnenKollision.join(', ')}).
        Beide gleichzeitig würden die Fernsteuerung verwirren.
        {onAnheften && angeheftet && <> <button type="button" data-ai-id="buehne-kollision-loesen" onClick={() => onAnheften(angeheftet.id, false)}>Angeheftete Bühne lösen</button></>}
      </div> : <ErrorBoundary componentName="Bühnenanordnung"><BuehnenArbeitsflaeche doc={e.doc as IJsonModel} bezug={e.bezug}
        factory={factory} onModel={e.sichtbar ? handleBuehnenModel : handleAngeheftetModel} sichtbar={e.sichtbar}
        angeheftet={e.angeheftet} veraltet={e.veraltet} onAnheften={onAnheften && e.bezug ? (an: boolean) => onAnheften(e.bezug!.id, an) : undefined} /></ErrorBoundary>}
    </div>;
  }) : null;

  /*
   * DIE BUEHNE HAENGT NICHT AM ARBEITSBEREICH.
   *
   * Frueher stand hier ein blosses `return` — wer kein Arbeitsbereichs-Layout
   * hatte, sah "Loading layout..." und die Buehne DARUEBER kam nie zum Zug,
   * obwohl sie ein eigenes, vollstaendig unabhaengiges Dokument ist (siehe
   * `buehne`-Prop oben). Genau das traf Rafael am 08.09.2026: devops.json war
   * kaputt, also blieb auch die Entscheidungs-Buehne dieser Karte schwarz.
   * Die Buehne wird deshalb auch dann gezeigt, wenn darunter (noch) nichts steht.
   */
  if (!model) {
    return (
      <div ref={shareRoot} style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {buehnenModel && isActive !== false && <div style={{position:'absolute',right:buehne ? 6 : 85,top:3,zIndex:12}}><ShareStageButton model={buehnenModel} root={() => shareRoot.current} title={projectId} buehneId={buehnenBezug?.id} /></div>}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 16, textAlign: 'center' }}>
          {layoutDefekt ? (
            <>
              <div data-ai-id="layout-defekt" style={{ color: 'var(--tn-red, var(--tn-red))', fontSize: 13 }}>
                Layout „{layoutId}" laesst sich nicht bauen.
              </div>
              <div style={{ color: 'var(--tn-text-muted)', fontSize: 11, maxWidth: 520, lineHeight: 1.5 }}>
                {layoutDefekt} — die gespeicherte Datei ist kein vollstaendiges Layout-Dokument
                (erwartet werden `global`, `borders` und `layout`). Ueber „Layout" laesst sich ein
                neues herrichten; der Arbeitsbereich wird dabei nicht ueberschrieben, solange nichts
                gespeichert wird.
              </div>
            </>
          ) : (
            <div style={{ color: 'var(--tn-text-muted)', fontSize: 12 }}>Loading layout...</div>
          )}
        </div>

        {buehnenElement}
      </div>
    );
  }

  return (
    <div ref={shareRoot} style={{ flex: 1, position: 'relative', minHeight: 0, contain: 'layout style' }}>
      {isActive !== false && <div style={{position:'absolute',right:buehne ? 6 : 85,top:3,zIndex:12}}><ShareStageButton model={buehnenModel ?? model} root={() => shareRoot.current} title={projectId} buehneId={buehnenBezug?.id} /></div>}
      {layoutElement}

      {/*
        Scheitert ein Bauversuch, waehrend noch ein aelteres Modell steht (Bauplan
        uebernehmen, zuruecksetzen), bleibt die Flaeche brauchbar — aber sie sagt es.
        Frueher stand das nur in der Browser-Konsole, die niemand offen hat.
      */}
      {layoutDefekt && (
        <div
          data-ai-id="layout-defekt-streifen"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 12,
            background: 'var(--tn-bg-dark)', borderBottom: '1px solid var(--tn-red, var(--tn-red))',
            color: 'var(--tn-red, var(--tn-red))', fontSize: 11, padding: '4px 8px',
          }}
        >
          Layout „{layoutId}" liess sich nicht bauen: {layoutDefekt}
        </div>
      )}

      {/*
        Die Buehne liegt DARUEBER, sie ersetzt nichts. Der Arbeitsbereich darunter
        bleibt gemountet und laeuft weiter — webviews, Terminals und Chats verlieren
        weder Zustand noch Verbindung, genau wie beim Vorhang in App.tsx.

        z-index 11 + isolation, nicht 2: die Trenner des Arbeitsbereichs darunter stehen
        auf z-index 10 (flexlayout dark.css, .flexlayout__splitter) und lagen im selben
        Stapelkontext ueber der Buehne — Rafael sah „blaue Striche", Verschiebebalken des
        unsichtbaren Arbeitsbereichs mitten durch Seite und Chat (Bilder 07.09. zd14 und
        8h8j, Befund koordination/entscheidungen/2026-09-07-buehne-striche-befund.md).
        `isolation: isolate` haelt ausserdem alles, was die Buehne selbst stapelt, in
        ihrem eigenen Kontext, damit kein spaeterer z-index von unten je wieder durchsticht.
      */}
      {buehnenElement}

      {/* Floating toolbar */}
      <div style={{
        position: 'absolute', top: 6, right: 6, zIndex: 10,
        display: 'flex', gap: 4,
      }}>
        <button
          data-ai-id="layout-manager-open-builder"
          onClick={() => setShowBuilder(true)}
          title="Layout Builder"
          style={{
            background: 'var(--tn-bg-dark)', border: '1px solid var(--tn-border)',
            color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 13,
            padding: '3px 7px', borderRadius: 4, opacity: 0.7,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.7'; }}
        >
          ⊞
        </button>
        <button
          data-ai-id="layout-manager-reset-layout"
          onClick={handleResetLayout}
          title="Layout zuruecksetzen"
          style={{
            background: 'var(--tn-bg-dark)', border: '1px solid var(--tn-border)',
            color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 13,
            padding: '3px 7px', borderRadius: 4, opacity: 0.7,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.7'; }}
        >
          ↺
        </button>
      </div>

      {showBuilder && (
        <Suspense fallback={<PanelLoader />}>
          <LayoutBuilder
            workDir={workDir ?? ''}
            onApply={handleApplyLayout}
            onClose={() => setShowBuilder(false)}
          />
        </Suspense>
      )}
    </div>
  );
}
